/* tslint:disable:max-line-length */
import { Injectable } from '@angular/core';
import { AppConstants, LMToolbarActionTypeEnum } from '@utils/app.constants';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMFormatUtilService, LMUrlUtilService } from '@services/utils';
import { LMActionService } from '@services/lm-action.service';
import { LMToolbarServiceBase } from '@toolbar/lm-toolbar-service.base';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMListSpecUtilService } from './lm-list-spec-util.service';
import { LMContextService } from '@services/lm-context.service';
import { ListResource } from '@resources/list.resource';
import * as _ from 'lodash';
import { LMToolbarServiceAdapter } from '@toolbar/lm-toolbar-service-adapter';

/**
 * Manages building and setting of state for a lm-list.component toolbar.
 */
@Injectable()
export class LMListToolbarService extends LMToolbarServiceBase {

  constructor(
    private lmActionService: LMActionService,
    private lmContextService: LMContextService,
    private lmConditionUtilService: LMConditionUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmLoggerService: LMLoggerService,
    private lmListSpecUtilService: LMListSpecUtilService,
    public lmToolbarServiceAdapter: LMToolbarServiceAdapter,
    private lmUrlUtilService: LMUrlUtilService
  ) {
    super(lmToolbarServiceAdapter);
  }

  /**
   *
   * @param listSpec
   * @returns
   */
  public buildToolbarButtons(listSpec: LMListSpec, listStateInfo: LMListStateInfo, listUrlInfo: LMListUrlInfo): void {
    this.updateToolbar(listSpec, listStateInfo, listUrlInfo);
  }

  public updateToolbar(listSpec: LMListSpec, listStateInfo: LMListStateInfo, listUrlInfo: LMListUrlInfo): void {
    try {
      // -----------------------------------------------------------
      // Must rebuild the toolbar so that actions in the overflow
      // menu will be availabel for the selected row(s) of data.
      // TODO ppatton - look into building the static part of the
      // toolbar during a single build and only rebuilding the
      // dynamic actions menu in UpdateToolbar. May not be worth
      // the effort as I can't imagine it will save much processing
      // time.
      // -----------------------------------------------------------
      this.toolbarButtons = [];

      if (!listStateInfo.disableAllActions) {
        // Add processflow actions to the list of buttons.
        if (listStateInfo && listStateInfo.dataViewSet && listSpec.listType !== 'select') {
          this.buildProcessFlowActionToolbarButtons(listSpec, listStateInfo);
        }

        if (listStateInfo.specialActions) {
          this.toolbarButtons = this.actionRefArrayToToolbarButtonArray(listSpec, listStateInfo.specialActions);
          this.toolbarButtons.forEach((button: LMToolbarButton) => button.actionReference.isSpecialAction = true);
        }

        if (listSpec.busListSpec.implementsCubeView) {
          const cubeButtons = this.buildCubeToolbarButtons(listSpec, listStateInfo);
          this.toolbarButtons = [ ...this.toolbarButtons, ...cubeButtons ];
          this._updateToolbarState(this.toolbarButtons, listSpec, listStateInfo, listUrlInfo);
          this.fireToolbarObservable();
          return;
        }

        if (listSpec.listType === 'select' && this.lmContextService.casualMode()) {
          this.buildSelectListCasualModeToolbarButtons(listSpec);
        } else if (listStateInfo.isSelectListInSelectionMode) {
          this.buildSelectListToolbarButtons(listSpec, listStateInfo);
        } else if (listSpec.busListSpec.toolbar) {
          this.buildCalledOutToolbarButtons(listSpec, listStateInfo);
        } else if (listSpec.busListSpec.implementsGalleryView && this.lmListSpecUtilService.allowHorizontalPaging(listSpec)) {
          this.buildGalleryToolbarButtons(listSpec, listStateInfo);
        } else {
          this.buildStandardToolbarButtons(listSpec, listStateInfo);
        }
      }

      // Even if all actions are disabled, the cubeview detail section list will display pvperiods menu
      // to filter data based on period view period.
      if (this.lmListSpecUtilService.isDetailSection(listSpec)
        && listStateInfo.fromDataView && listStateInfo.fromDataView.cubeInfo) {
        // Add the period view period button as the first item.
        this.toolbarButtons = [ ...this.buildCubeViewDetailSectionToolbarItem(listSpec, listStateInfo), ...this.toolbarButtons ];
      }

      if (!this.toolbarButtons || this.toolbarButtons.length === 0) {
        this.fireToolbarObservable();
        return;
      }

      if (listSpec.busListSpec.implementsGalleryView && this.lmListSpecUtilService.allowHorizontalPaging(listSpec)) {
        this.updateGalleryToolbarState(listStateInfo);
      } else {
        this._updateToolbarState(this.toolbarButtons, listSpec, listStateInfo, listUrlInfo);
      }

      this.fireToolbarObservable();

      if (this.secondaryToolbarButtons) {
        this._updateToolbarState(this.secondaryToolbarButtons, listSpec, listStateInfo, listUrlInfo);
        this.fireSecondaryToolbarObservable();
      }
    } catch (e) {
      this.lmLoggerService.error(e);
    }
  }

  /**
   * Build an
   * @param listSpec
   * @param dataView need dataView to resolve the label MessageRule
   * @returns
   */
  public getToolbarSearchField(listSpec: LMListSpec): LMToolbarSearchField {
    let keywordSearch: LMKeywordSearch = listSpec.busListSpec.keywordSearch;
    if (!keywordSearch) {
      if (listSpec.busListSpec.searchFields && listSpec.busListSpec.searchFields.useSearchAsFilter) {
        keywordSearch = {
          fieldId: listSpec.busListSpec.searchFields.fields[0].fieldId,
          label: {
            resourceValue: Soho.Locale.translate('Search')
          }
        };
      } else {
        return null;
      }
    }

    const label = this.lmFormatUtilService.formatMessageRule(keywordSearch.label, null);
    return { fieldId: keywordSearch.fieldId, label, collapsible: !keywordSearch.displayAlways, value: '', collapseSize: this.calculateSearchFieldSize };
  }

  // Calculates the size of the Searchfield's closed state based on the
  // length of the current search term.
  public calculateSearchFieldSize(api) {
    const charWidth = 8;
    const frontPadding = 30;
    let buttonSize = 0;
    const contents = api.element.val();

    if ((api.categoryButton instanceof $) && api.categoryButton.length > 0) {
      const buttonStyle = window.getComputedStyle(api.categoryButton[0]);
      const buttonWidth = api.categoryButton.width();
      const buttonBorder = parseInt(buttonStyle.borderLeftWidth, 10) * 2;
      const buttonPadding = parseInt(buttonStyle.paddingLeft, 10) +
        parseInt(buttonStyle.paddingRight, 10);

      buttonSize += (buttonWidth + buttonBorder + buttonPadding + 4);
    }

    // Can't be bigger than 225px.
    const maxSize = 225;
    const targetSize = (buttonSize > 0 ? buttonSize : frontPadding) + (charWidth * contents.length);

    return targetSize < maxSize ? targetSize : maxSize;
  }

  /**
   * Transient build of a menu. This is build and used only when a right mouse button is clicked.
   */
  public getContextMenu(listSpec: LMListSpec, listStateInfo: LMListStateInfo): Array<LMToolbarButton> {
    // disallow right click menus for casual mode.
    if (this.lmContextService.casualMode() || listStateInfo.disableAllActions) {
      return [];
    }

    const useCalledOutToolbar = listSpec.busListSpec.toolbar && !listSpec.busListSpec.implementsOrgChartView && !listSpec.busListSpec.implementsCubeView;
    let calledOutActionRefs: LMBusActionReference[] = [];
    let calledOutMenuItems: Array<LMToolbarButton> = [];
    if (useCalledOutToolbar) {
      const singleSelectActionRefs: LMBusActionReference[] = [];
      if (listStateInfo.isSelectListInSelectionMode || listStateInfo.isSelectListInEditMode) {
        // Add edit mode menu item for a select list
        const editModeActionRef = this.getStandardToolbarActions().editMode;
        editModeActionRef.isChecked = listStateInfo.isSelectListInEditMode;
        singleSelectActionRefs.push(editModeActionRef);
        singleSelectActionRefs.push(this.getStandardSeparatorMenuItem());
      }

      // ---------------------------------------------------------------
      // if called out toolbar put those action first
      // ---------------------------------------------------------------
      calledOutActionRefs = this.getCalledOutToolbarActionRefs(listSpec, listStateInfo);
      calledOutActionRefs = [ ...singleSelectActionRefs, ...calledOutActionRefs ];

      if (calledOutActionRefs.length > 0) {
        // Need to filter out helper list, so it doesn't show up in a context menu
        calledOutActionRefs = calledOutActionRefs.filter(actionRef => actionRef.id !== AppConstants.STANDARD_TOOLBAR_HELPERLIST);
      }

      if (calledOutActionRefs.length > 0) {
        if (this.hasCreateMenu(listSpec.busListSpec.toolbar)) {
          const createActionSpecs: Array<LMActionSpec> = this.lmActionService.getCreateActions(listSpec.busClassSpec.actions, listStateInfo.dataViewSet.header.actionIndexes);
          if (createActionSpecs?.length > 1) {
            // replace the create menu item with the create actions
            const createActionRefs: Array<LMBusActionReference> = this.actionSpecsToActionRefsSorted(createActionSpecs);
            const index = calledOutActionRefs.findIndex(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON);
            calledOutActionRefs.splice(index, 1, ...createActionRefs);
          }
        }
      }

      calledOutMenuItems = this.actionRefArrayToToolbarButtonArray(
        listSpec, calledOutActionRefs, this.lmListSpecUtilService.isCardViewWithDetailSections(listSpec));

      const userListViewMenuButton = calledOutMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_MENU);
      if (userListViewMenuButton) {
        const userListView: Array<LMBusActionReference> = [ { id: 'noop', type: 'noop', label: 'Not yet implemented' } ];
        userListViewMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, userListView);
      }
    }

    // 1. build the context menu buttons
    // 2. set the enabled and visibility state
    // 3. return the context menu buttons
    let contextMenuItems = (listSpec.busListSpec.implementsCubeView)
      ? this.getAllActionsMenuItemsForCubeView(listSpec, listStateInfo, calledOutActionRefs, true, true)
      : this.getAllActionsMenuItems(listSpec, listStateInfo, calledOutActionRefs, true, true);

    // has called out actions
    if (useCalledOutToolbar) {
      if (listStateInfo.calledOutActionsOnly) {
        contextMenuItems = calledOutMenuItems;
      } else {
        contextMenuItems = [ ...calledOutMenuItems, ...contextMenuItems ];
      }
    }

    this._updateToolbarState(contextMenuItems, listSpec, listStateInfo);
    return contextMenuItems;
  }

  // ----------------------------------------------------------------------------------------
  // PRIVATE METHODS
  // ----------------------------------------------------------------------------------------

  /**
   *
   * @param toolbarButtons
   * @param dataViewSet
   * @param selectedRows
   * @returns
   */
  private _updateToolbarState(toolbarButtons: Array<LMToolbarButton>, listSpec: LMListSpec, listStateInfo: LMListStateInfo, listUrlInfo?: LMListUrlInfo): void {
    try {
      toolbarButtons.forEach((toolbarButton: LMToolbarButton) => {
        // Update state for non menu buttons
        this.setStateByActionType(listSpec, listStateInfo, toolbarButton, listUrlInfo);

        // Rebuild menus
        this.rebuildMenuButtonMenu(toolbarButton, listSpec, listStateInfo);
      });

      toolbarButtons.map(toolbarButton => {
        if (!toolbarButton.menu) {
          return;
        }
        toolbarButton.menu.map(item => this.setStateByActionType(listSpec, listStateInfo, item, listUrlInfo));
      });

    } catch (e) {
      this.lmLoggerService.error(e);
    }
  }

  public updateGalleryToolbarState( listStateInfo: LMListStateInfo): void {
    this.toolbarButtons.map((button) => {
      if (button.actionReference.type === LMToolbarActionTypeEnum.GALLERY_NEXT) {
        listStateInfo.galleryViewState.isScrollEndPromise.then((isScrollEnd) => {
          button.state.disabled = isScrollEnd;
          this.fireToolbarObservable();
        });
      }

      if (button.actionReference.type === LMToolbarActionTypeEnum.GALLERY_PREVIOUS) {
        listStateInfo.galleryViewState.isScrollStartPromise.then((isScrollStart) => {
          button.state.disabled = isScrollStart;
          this.fireToolbarObservable();
        });
      }
    });
  }

  private rebuildMenuButtonMenu(toolbarButton: LMToolbarButton, listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    const singleChartItem = this.getChartMenuActionRefs(listSpec, listStateInfo).length === 1;

    if (toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON
    || (toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_CREATE && listStateInfo.createWithinEnabled)) {
      this.buildCreateMenu(toolbarButton, listSpec, listStateInfo);
    }

    if ((toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_CHARTMENU_BUTTON
      || toolbarButton.actionReference.id === AppConstants.CUBE_TOOLBAR_CHARTS) &&
      !singleChartItem
    ) {
      this.buildChartsMenu(toolbarButton, listSpec, listStateInfo);
    }

    if (toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_SORT) {
      this.buildSortMenu(toolbarButton, listSpec, listStateInfo);
    }

    if (toolbarButton.actionReference.id === AppConstants.CUBE_TOOLBAR_OPTIONS) {
      this.getCubeOptionsMenu(toolbarButton, listSpec, listStateInfo);
      const noCreateReportInConfig = this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_CreateReport);
      if (noCreateReportInConfig) {
        const relatedReportButton = toolbarButton.menu.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_RELATEDREPORT);
        if (relatedReportButton) {
          const validrelatedReporActionRefs: Array<LMBusActionReference> = [ { id: 'noop', type: 'noop', label: 'Not yet implemented' } ];
          relatedReportButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, validrelatedReporActionRefs);
        }
        const personalizeButton = toolbarButton.menu.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_PERSONALIZE);
        if (personalizeButton) {
          const validPersonalizeActionRefs: Array<LMBusActionReference> = [ { id: 'noop', type: 'noop', label: 'Not yet implemented' } ];
          personalizeButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, validPersonalizeActionRefs);
        }
      }
    }

    if (toolbarButton.actionReference.id === AppConstants.CUBE_TOOLBAR_PERIODVIEW) {
      this.getCubePeriodViewMenu(toolbarButton, listSpec, listStateInfo);
    }

    if (toolbarButton.actionReference.id === AppConstants.CUBE_TOOLBAR_PIVOTVIEWS) {
      this.getCubePivotViewMenu(toolbarButton, listSpec, listStateInfo);
    }

    if (toolbarButton.actionReference.id === AppConstants.CUBE_TOOLBAR_COLUMNVIEWS) {
      this.getCubeColumnViewMenu(toolbarButton, listSpec, listStateInfo);
    }

    if (toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_HELPERLISTMENU_BUTTON) {
      this.buildHelperListMenu(toolbarButton, listSpec, listStateInfo);
    }
  }

  private setStateByActionType(listSpec: LMListSpec, listStateInfo: LMListStateInfo, toolbarButton: LMToolbarButton, listUrlInfo?: LMListUrlInfo): void {
    let actionType: string;
    if (toolbarButton.actionReference) {
      actionType = toolbarButton.actionReference.type;
    } else if (toolbarButton.actionSpec) {
      actionType = toolbarButton.actionSpec.type;

      if (AppConstants.STANDARD_TOOLBAR_TYPES.includes(actionType)) {
        actionType = AppConstants.BUTTON_TYPE_STANDARD_ACTION;
      }
    }

    switch (actionType) {
      case AppConstants.BUTTON_TYPE_STANDARD_ACTION:
      case AppConstants.BUTTON_TYPE_HELPERLIST:
        this.standardActionState(listSpec, listStateInfo, toolbarButton);
        break;

      case AppConstants.BUTTON_TYPE_FORMINVOKE:
        toolbarButton.state = this.getFormInvokeState(listStateInfo, toolbarButton);
        break;

      case AppConstants.BUTTON_TYPE_HTTPURL:
        this.httpUrlActionState(listStateInfo, toolbarButton);
        break;

      case AppConstants.BUTTON_TYPE_NAVIGATION:
        const specFields = (listSpec && listSpec.busListSpec) ? listSpec.busListSpec.fields : null;
        this.navigationActionState(listStateInfo, toolbarButton, specFields);
        break;

      case AppConstants.BUTTON_TYPE_ACTION:
        this.busClassActionState(listSpec, listStateInfo, toolbarButton);
        break;

      case AppConstants.BUTTON_TYPE_PROCESSFLOW:
        toolbarButton = this.processFlowActionState(listSpec, listStateInfo, toolbarButton);
        break;

      case AppConstants.BUTTON_TYPE_RELATED:
        toolbarButton.state.disabled = false;
        toolbarButton.state.visible = true;
        if (toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_MENU) {
          const url = this.lmUrlUtilService.buildUserListViewFetchUrl(listUrlInfo, listSpec);
          toolbarButton.actionReference.userListViewUrl = url;
          toolbarButton.actionReference.appliedUserListView = listStateInfo.userListViewItem?.userListView;
          toolbarButton.actionReference.disableSave = listStateInfo.disableSaveInUserListViewMenu;
        }
        break;

      case AppConstants.BUTTON_TYPE_CUBE:
        this.cubeActionState(listSpec, listStateInfo, toolbarButton);
        break;

      case AppConstants.BUTTON_TYPE_FORMBUTTON:
        toolbarButton.state.disabled = false;
        toolbarButton.state.visible = true;
        break;

      case AppConstants.CSS_MENU_SEPARATOR:
        toolbarButton.state.disabled = false;
        toolbarButton.state.visible = true;
        break;
    }
  }

  private getAllActionsMenuItemsForCubeView(
    listSpec: LMListSpec,
    listStateInfo: LMListStateInfo,
    excludedActionRefs?: Array<LMBusActionReference>,
    includeCreateActions?: boolean,
    isContextMenu?: boolean,
  ): Array<LMToolbarButton> {
    // This is used only on right click, context menu for cubes.
    // Cubes will have open action only. For now, the other menu items such as options,
    // drill around, create report, personalize or search are not available.
    if (!listSpec.busListSpec.implementsCubeView || listStateInfo.selectedDataView === undefined) {
      return [];
    }

    const validActionRefs: Array<LMBusActionReference> = [];
    if (!listSpec.busListSpec.openActionRestricted
        && !excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_OPEN )
        && listStateInfo.selectedDataView.openFormResId !== undefined) {
      validActionRefs.push(this.getStandardToolbarActions().open);
    }

    // reparent menu. Add this only if child hierarchy dimensions are present.
    if (listSpec.busListSpec.hasChildHierarchyDims) {
      validActionRefs.push({
        id: AppConstants.CUBE_TOOLBAR_DRILLDOWN_DIMENSIONS,
        automationId: AppConstants.CUBE_TOOLBAR_DRILLDOWN_DIMENSIONS,
        type: 'cube',
        label: Soho.Locale.translate('DrillDown')
      });
    }

    const allActionsMenuItems = this.createToolbarMenuItemsFromActionReferences(listSpec, validActionRefs, true);
    return allActionsMenuItems;
  }

  private getAllActionsMenuItems(
    listSpec: LMListSpec,
    listStateInfo: LMListStateInfo,
    excludedActionRefs?: Array<LMBusActionReference>,
    includeCreateActions?: boolean,
    isContextMenu?: boolean,
  ): Array<LMToolbarButton> {
    if (listSpec.listType === 'select' && this.lmContextService.casualMode()) {
      return [];
    }

    const validActionRefs: Array<LMBusActionReference> = [];

    if (listStateInfo.isSelectListInSelectionMode || listStateInfo.isSelectListInEditMode) {
      if (!excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_EDITMODE)) {
        // Add edit mode menu item for a select list
        const editModeActionRef = this.getStandardToolbarActions().editMode;
        editModeActionRef.isChecked = listStateInfo.isSelectListInEditMode;
        validActionRefs.push(editModeActionRef);
      }
    }

    // all actions for a more or right click menu
    const allValidActionRefs: Array<LMBusActionReference> = this.getAllValidActionReferences(
      listSpec,
      listStateInfo,
      excludedActionRefs,
      includeCreateActions,
      this.lmContextService.hasAsOfDateInPast(),
      isContextMenu
    );
    if (validActionRefs.length > 0 && allValidActionRefs.length > 0) {
      validActionRefs.push(this.getStandardSeparatorMenuItem());
    }
    validActionRefs.push(...allValidActionRefs);

    // OPTIONS MENU
    validActionRefs.push(this.getStandardToolbarActions().options);

    // DRILL MENU
    if (!listStateInfo.isSelectListInSelectionMode) {
      validActionRefs.push(this.getStandardToolbarActions().drillAroundMenu);
    }

    if (validActionRefs.length === 0) {
      return [];
    }

    // ----------------------------------------------------------
    // turn valid action refs into toolbar buttons.
    // ----------------------------------------------------------
    let allActionsMenuItems = this.createToolbarMenuItemsFromActionReferences(listSpec, validActionRefs, true);

    // Filter out page level actions on a context menu for org chart
    if (isContextMenu && listSpec.busListSpec.implementsOrgChartView) {
      allActionsMenuItems = allActionsMenuItems.filter((item) => item.actionReference.id !== AppConstants.STANDARD_TOOLBAR_SEARCH);
    }

    // --------------------------------------------------------------
    // Don't create an options menu button if in casual mode.
    // --------------------------------------------------------------
    if (!this.lmContextService.casualMode()) {
      const chartsMenuButton = allActionsMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_CHARTMENU_BUTTON);
      const singleChartItem = this.getChartMenuActionRefs(listSpec, listStateInfo).length === 1;

      if (chartsMenuButton && !singleChartItem) {
        this.buildChartsMenu(chartsMenuButton, listSpec, listStateInfo);
      }

      const optionsMenuButton = allActionsMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_OPTIONSMENU_BUTTON);
      if (optionsMenuButton) {
        this.buildOptionsMenu(optionsMenuButton, listSpec, listStateInfo, true);

        const relatedReportButton = optionsMenuButton.menu.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_RELATEDREPORT);
        if (relatedReportButton) {
          const validrelatedReporActionRefs: Array<LMBusActionReference> = [ { id: 'noop', type: 'noop', label: 'Not yet implemented' } ];
          relatedReportButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, validrelatedReporActionRefs);
        }

        const personalizeButton = optionsMenuButton.menu.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_PERSONALIZE);
        if (personalizeButton) {
          const validPersonalizeActionRefs: Array<LMBusActionReference> = [ { id: 'noop', type: 'noop', label: 'Not yet implemented' } ];
          personalizeButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, validPersonalizeActionRefs);
        }
      }

      const drillMenuButton = allActionsMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_RELATEDMENU_BUTTON);
      if (drillMenuButton) {
        this.buildDrillMenuPlaceHolderItem(listSpec, drillMenuButton);
      }

      const userListViewMenuButton = allActionsMenuItems.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_MENU);
      if (userListViewMenuButton) {
        const userListView: Array<LMBusActionReference> = [ { id: 'noop', type: 'noop', label: 'Not yet implemented' } ];
        userListViewMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, userListView);
      }
    }

    return allActionsMenuItems;
  }

  /**
   *
   * @param toolbarButton
   * @param dataViewSet
   * @param selectedRows
   * @returns
   */
  private busClassActionState(listSpec: LMListSpec, listStateInfo: LMListStateInfo, toolbarButton: LMToolbarButton): void {
    let valid = false;
    let actionSpec: LMActionSpec;
    if (toolbarButton.actionReference.isSpecialAction) {
      if (listStateInfo.fromDataView.actionIndexes.includes(toolbarButton.actionReference.index)) {
        toolbarButton.state.disabled = false;
        toolbarButton.state.visible = true;
        return;
      } else {
        toolbarButton.state.disabled = true;
        toolbarButton.state.visible = false;
      }
      return;
    } else {
      actionSpec = listSpec.busClassSpec.actions[ toolbarButton.actionReference.index ];
    }

    const dataViewSet = listStateInfo.dataViewSet;

    if (dataViewSet === undefined) {
      this.lmLoggerService.warn('listStateInfo.dataViewSet is undefined in busClassActionState()');
      return;
    }

    const validInstanceActions: Array<LMActionSpec> =
        this.lmActionService.getValidListInstanceActions(
          listSpec.busClassSpec.actions, dataViewSet, listStateInfo.selectedRows);

    const validImportActions: Array<LMActionSpec> =
        this.lmActionService.getValidListImportActions(
          listSpec.busClassSpec.actions, dataViewSet);

    const validDeleteActions: Array<LMActionSpec> =
        this.lmActionService.getValidListDeleteActions(
          listSpec.busClassSpec.actions, dataViewSet, listStateInfo.selectedRows);

    switch (actionSpec.type) {
      case AppConstants.ACTION_TYPE_CREATE:
      case AppConstants.ACTION_TYPE_CREATE_WITHIN:
      case AppConstants.ACTION_TYPE_SET:
        // check actionSpec validity in the dataView
        valid = this.lmActionService.isValidAction(actionSpec, listSpec.busClassSpec.actions, dataViewSet.header.actionIndexes, undefined, false);

        // check for action button validity
        if (valid && toolbarButton.actionReference.validity) {
          // todo akila - passign in '' as a NOOP for specFields. not sure what is needed there as list has no specFields.
          valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataViewSet.header);
        }

        break;

      case AppConstants.ACTION_TYPE_UPDATE:
        if (!listStateInfo.selectedRows || listStateInfo.selectedRows.length !== 1) {
          // if no selection or more than 1 selection then valid = false
          valid = false;
        } else {
          // check actionSpec validity in the dataView
          const updateDataView: LMDataView = dataViewSet.data[ listStateInfo.selectedRows[ 0 ] ];

          if (updateDataView) {
            if (listSpec.isTemplateList) {
              const targetDataView = updateDataView.dataView;
              if (updateDataView.dataView.isAlreadyDefined) {
                valid = this.lmActionService.isValidAction(actionSpec, listSpec.busClassSpec.actions, targetDataView.actionIndexes, 'UPDATE', false);
              } else {
                valid = this.lmActionService.isValidAction(actionSpec, listSpec.busClassSpec.actions, targetDataView.actionIndexes, 'CREATE', false);
              }
            } else {
              valid = this.lmActionService.isValidAction(actionSpec, listSpec.busClassSpec.actions, updateDataView.actionIndexes, undefined, false);
            }

            // check for action button validity
            if (valid && toolbarButton.actionReference.validity) {
              // todo akila - passign in '' as a NOOP for specFields. not sure what is needed there as list has no specFields.
              valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, updateDataView);
            }
          }
        }

        break;

      case AppConstants.ACTION_TYPE_FUNCTION:
        const functionDataView: LMDataView = dataViewSet.data[ listStateInfo.selectedRows[ 0 ] ];

        if (functionDataView) {
          valid = !!(validInstanceActions.find(item => item.name === actionSpec.name));

          if (valid && toolbarButton.actionReference.validity) {
            valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, functionDataView);
          }
        }

        break;

      case AppConstants.ACTION_TYPE_IMPORT:
        valid = !!(validImportActions.find(item => item.name === actionSpec.name));
        break;

      case AppConstants.ACTION_TYPE_DELETE:
        valid = !!(validDeleteActions.find(item => item.name === actionSpec.name));

        // does a validity condition exist for delete?
        if (valid && toolbarButton.actionReference.validity) {
          // iterate the selected rows to determine the overall action validity
          // eslint-disable-next-line @typescript-eslint/prefer-for-of
          for (let i = 0; i < listStateInfo.selectedRows.length; i++) {
            const dataView = dataViewSet.data[ listStateInfo.selectedRows[ i ] ];
            if (dataView !== undefined) {
              const targetDataView = listSpec.isTemplateList ? dataView.dataView : dataView;
              valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, targetDataView);
              if (!valid) {
                break;
              }
            }
          }
        }

        break;
    }

    // disabled if not valid
    toolbarButton.state.disabled = !valid;

    const hideWhenInvalid = (listStateInfo.isSelectListInSelectionMode) ? true : toolbarButton?.actionReference?.hideWhenInvalid;
    toolbarButton.state.visible = !(!valid && hideWhenInvalid);
  }

  /**
   *
   * @param toolbarButtons
   * @param dataViewSet
   * @returns
   */
  private buildCreateMenu(createMenuButton: LMToolbarButton, listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    try {
      // Create Action is never valid in a template list!
      // the UI uses the update or open action and dynamically
      // switches to a create action if the template target dataView is
      // not defined
      if (listSpec.isTemplateList) {
        createMenuButton.menu = [];
      } else {
        const actions = listSpec.busClassSpec.actions;
        const createActionRefs: Array<LMBusActionReference> = this.actionSpecsToActionRefs(
          this.lmActionService.getCreateActions(actions, listStateInfo.dataViewSet.header.actionIndexes));

        if (listStateInfo.inlineCreateEnabled && listSpec.busListSpec.allowFormCreate) {
          createActionRefs.push(this.getStandardActionMenuItems().inlineCreate);
        }

        if (listStateInfo.createWithinEnabled && (listSpec.busListSpec.implementsTreeView || listSpec.busListSpec.implementsOrgChartView)) {
          createActionRefs.push(this.getStandardActionMenuItems().createWithin);
        }

        createActionRefs.sort((actionRef1: LMBusActionReference, actionRef2: LMBusActionReference) => actionRef1.label.localeCompare(actionRef2.label));
        createMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, createActionRefs);
      }
    } catch (e) {
      this.lmLoggerService.error(e);
    }
  }

  /**
   *
   * @param toolbarButtons
   * @param dataViewSet
   * @returns
   */
  private buildHelperListMenu(helperListMenuButton: LMToolbarButton, listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    try {
      const helperListActionRefs: Array<LMBusActionReference> = [];

      let helperListIndex = 0;
      listSpec.busListSpec.helperLists.forEach((helperList => {
        const listTitle = helperList.listSpec.busListSpec.title;
        const actionReference: LMBusActionReference = {
          id: AppConstants.STANDARD_TOOLBAR_HELPERLIST,
          automationId: AppConstants.STANDARD_TOOLBAR_HELPERLIST,
          index: helperListIndex,
          label: helperList.helperTitle,
          type: 'helperList',
          tooltip: Soho.Locale.translate('HelperList'),
          validity: helperList.validity
        };

        helperListActionRefs.push(actionReference);
        helperListIndex++;
      }));
      helperListMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, helperListActionRefs);

      let anyValid = false;
      let anyVisible = false;

      helperListMenuButton.menu.forEach((helperToolbarButton: LMToolbarButton) => {

        const visible = true;
        let valid = true;

        const helperList = listSpec.busListSpec.helperLists[helperToolbarButton.actionReference.index];
        if (helperList.visibility) {
          valid = this.lmConditionUtilService.isValid(helperList.visibility, listStateInfo.fromDataView);
        }

        if (valid && helperList.validity) {
          valid = this.lmConditionUtilService.isValid(helperList.validity, listStateInfo.fromDataView);
        }

        // check if the called out action has a validity condition
        // this is defined on the list toolbar and is resolved with the dataViewSet
        if (valid && helperToolbarButton.actionReference.validity) {
          valid = this.lmConditionUtilService.isValid(helperToolbarButton.actionReference.validity, listStateInfo.dataViewSet.header);
        }
        // disabled if invalid
        helperToolbarButton.state.disabled = !valid;

        if (helperToolbarButton.actionReference && visible) {
          helperToolbarButton.state.visible = !(helperToolbarButton.actionReference.hideWhenInvalid && !valid);
        } else {
          helperToolbarButton.state.visible = visible;
        }
        anyValid = !helperToolbarButton.state.disabled || anyValid ? true : false;
        anyVisible = helperToolbarButton.state.visible || anyVisible ? true : false;
      });

      // disabled menu button if invalid
      helperListMenuButton.state.disabled = !anyValid;
      helperListMenuButton.state.visible = anyVisible;
    } catch (e) {
      this.lmLoggerService.error(e);
    }
  }

  private buildChartsMenu(chartMenuButton: LMToolbarButton, listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    try {
      chartMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, this.getChartMenuActionRefs(listSpec, listStateInfo));
    } catch (e) {
      this.lmLoggerService.error(e);
    }
  }

  private getCubeOptionsMenu(toolbarButton: LMToolbarButton, listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    const context = this.lmContextService.getContext();
    const disallowExport = context.disallowExport;
    const configuredFeatures = context.configuredFeatures;
    const noPdfInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_ExportToPDF) !== -1;
    const noCsvInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_ExportToCSV) !== -1;
    const noCreateReportInConfig = this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_CreateReport);
    const noConfigInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_Configuration) !== -1;
    const noPersonlizationInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_Personalization) !== -1;
    const noLimitedInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_LimitedListPersonalization) !== -1;

    const actionRefs = [];
    actionRefs.push(this.getStandardOptionsMenuItems().refresh);
    actionRefs.push({
      id: AppConstants.CUBE_TOOLBAR_OPTIONS_CUBESTATUS,
      automationId: AppConstants.CUBE_TOOLBAR_OPTIONS_CUBESTATUS,
      type: 'cube',
      label: Soho.Locale.translate('CheckCubeStatus'),
      tooltip: Soho.Locale.translate('CheckCubeStatus')
    });

    // actionRefs.push({ id: 'separator', type: 'separator' });

    if (!disallowExport && noPdfInConfig) {
      const printToFile = this.getStandardOptionsMenuItems().printToFile;
      printToFile.type = 'cube';
      actionRefs.push(printToFile);
    }

    if (noCsvInConfig && listStateInfo.dataViewSet) {
      const exportToCSV = this.getStandardOptionsMenuItems().exportToCSV;
      exportToCSV.type = 'cube';
      actionRefs.push(exportToCSV);
    }

    if (noCreateReportInConfig) {
      actionRefs.push({ id: 'separator', type: 'separator' });
      actionRefs.push(this.getStandardOptionsMenuItems().createReport);
      actionRefs.push(this.getStandardOptionsMenuItems().relatedReport);
    }

    if (noConfigInConfig || noPersonlizationInConfig || noLimitedInConfig) {
      actionRefs.push(this.getStandardOptionsMenuItems().personalize);
    }

    toolbarButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, actionRefs);
  }

  private getCubePeriodViewMenu(toolbarButton: LMToolbarButton, listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    const actionRefs: Array<LMBusActionReference> = [];
    const currentIndex = (listStateInfo.dataViewSet && listStateInfo.dataViewSet.cubeInfo && listStateInfo.dataViewSet.cubeInfo.periodView)
      ? listSpec.cubeOptions.pCommands.indexOf(listStateInfo.dataViewSet.cubeInfo.periodView) : 0;

    listSpec.cubeOptions.periods.forEach((element, index, array) => {
      actionRefs.push({
        id: AppConstants.CUBE_TOOLBAR_PERIODVIEW,
        automationId: AppConstants.CUBE_TOOLBAR_PERIODVIEW + '-' + listSpec.cubeOptions.pCommands[index],
        type: 'cube',
        label: element,
        periodViewCommand: listSpec.cubeOptions.pCommands[index],
        isChecked: (currentIndex === index) ? true : null
      });
    });

    toolbarButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, actionRefs);
  }

  private getCubePivotViewMenu(toolbarButton: LMToolbarButton, listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    const actionRefs: Array<LMBusActionReference> = [];
    let pivotView;
    if (listStateInfo.dataViewSet.cubeInfo.pivotView) {
      pivotView = listSpec.cubeOptions.pivotViews.find((p) => p.name === listStateInfo.dataViewSet.cubeInfo.pivotView);
    } else {
      pivotView = listSpec.cubeOptions.pivotViews.find((p) => p.default !== undefined && p.default);
    }
    if (!pivotView) {
      pivotView = listSpec.cubeOptions.pivotViews[0];
    }
    listSpec.cubeOptions.pivotViews.forEach((element) => {
      actionRefs.push({
        id: AppConstants.CUBE_TOOLBAR_PIVOTVIEWS,
        automationId: AppConstants.CUBE_TOOLBAR_PIVOTVIEWS,
        type: 'cube',
        label: element.label,
        pivotViewCommand: element.name,
        isChecked: (pivotView.name === element.name) ? true : null
      });
    });
    toolbarButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, actionRefs);
  }

  private getCubeColumnViewMenu(toolbarButton: LMToolbarButton, listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    const actionRefs: Array<LMBusActionReference> = [];
    let columnView;
    if (listStateInfo.dataViewSet.cubeInfo.columnView) {
      columnView = listSpec.cubeOptions.columnViews.find((c) => c.name === listStateInfo.dataViewSet.cubeInfo.columnView);
    } else {
      columnView = listSpec.cubeOptions.columnViews.find((c) => c.default !== undefined && c.default);
    }
    if (!columnView) {
      columnView = listSpec.cubeOptions.columnViews[0];
    }

    listSpec.cubeOptions.columnViews.forEach((element, index) => {
      actionRefs.push({
        id: AppConstants.CUBE_TOOLBAR_COLUMNVIEWS,
        automationId: AppConstants.CUBE_TOOLBAR_COLUMNVIEWS + '-' + element.name,
        type: 'cube',
        label: element.label,
        columnViewCommand: element.name,
        isChecked: (columnView.name === element.name) ? true : null
      });
    });
    toolbarButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, actionRefs);
  }

  private buildDrillMenuPlaceHolderItem(listSpec: LMListSpec, drillMenuButton: LMToolbarButton): void {
    const validActionRefs: Array<LMBusActionReference> = [ { id: 'noop', type: 'noop', label: 'Not yet implemented' } ]; // Soho.Locale.translate('NoDrillsAvailable') }];
    drillMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, validActionRefs);
  }

  private buildSortMenu(sortButton: LMToolbarButton, listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {

    let sortActionRefs = this.getSortMenuActionRefs(listSpec, listStateInfo);
    if (sortActionRefs.length > 0) {
      sortActionRefs = [ ...sortActionRefs, ...this.getStandardSortMenuItems(listStateInfo.listIdPrefix) ];
      sortButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, sortActionRefs);

      let ascendingCheckString = AppConstants.STANDARD_TOOLBAR_SORT_ORDER_ASCENDING;
      if (!listStateInfo.dataViewSet.pagingInfo.isAscending) {
        ascendingCheckString = AppConstants.STANDARD_TOOLBAR_SORT_ORDER_DESCENDING;
      }

      sortButton.menu
        .filter((menuItem: LMToolbarButton) =>
          menuItem.actionReference.name === listStateInfo.dataViewSet.pagingInfo.sortOrderName || menuItem.actionReference.id === ascendingCheckString)
        .forEach((menuItem: LMToolbarButton) => menuItem.isChecked = true);
    } else {
      sortButton.menu = null;
    }
  }

  private buildOptionsMenu(optionsMenuButton: LMToolbarButton, listSpec: LMListSpec, listStateInfo: LMListStateInfo, moreActionsMenu?: boolean): void {
    const optionsMenuActionRefs = this.getOptionsMenuActionRefs(listSpec, listStateInfo, moreActionsMenu);
    optionsMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, optionsMenuActionRefs);
  }

  /**
   * Convert cardView displayField sortOrders to LMBusActionReference objects
   */
  private getSortMenuActionRefs(listSpec: LMListSpec, listStateInfo: LMListStateInfo): Array<LMBusActionReference> {
    if (this.lmListSpecUtilService.isListViewWithSorts(listSpec)) {
      return this.lmListSpecUtilService.cardViewSortOrders(listSpec).map(sortOrder => this.sortOrderToActionRef(sortOrder));
    } else if (this.lmListSpecUtilService.isGridWithCardViewSorts(listSpec)) {
      return this.lmListSpecUtilService.gridCardViewSortOrders(listSpec).map(sortOrder => this.sortOrderToActionRef(sortOrder));
    }
    return [];
  }

  /**
   * Make a LMBusActionReference object from a LMCardSortOrder object
   */
  private sortOrderToActionRef(sortOrder: LMCardSortOrder): LMBusActionReference {
    const sortOrderId = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1 ?
      AppConstants.STANDARD_TOOLBAR_SORT_ORDER_MENUITEM : sortOrder.sortOrderId;

    return <LMBusActionReference> {
      id:        AppConstants.STANDARD_TOOLBAR_SORT_ORDER_MENUITEM,
      automationId: AppConstants.STANDARD_TOOLBAR_SORT_ORDER_MENUITEM,
      elementId: sortOrderId,
      type:      'standard',
      label:     sortOrder.sortOrderLabel,
      name:      sortOrder.sortOrderName
    };
  }

  private getOptionsMenuActionRefs(listSpec: LMListSpec, listStateInfo: LMListStateInfo, moreActionsMenu?: boolean): Array<LMBusActionReference> {
    if (listSpec?.listType === 'select' && this.lmContextService.casualMode()) {
      return;
    }

    try {
      const context = this.lmContextService.getContext();
      const disallowExport = context.disallowExport;
      const configuredFeatures = context.configuredFeatures;
      const noPdfInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_ExportToPDF) !== -1;
      const noCsvInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_ExportToCSV) !== -1;
      const noCreateReportInConfig = this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_CreateReport); // this.lmContextService.isCreateReportEnabled();
      const noConfigInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_Configuration) !== -1;
      const noPersonlizationInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_Personalization) !== -1;
      const noLimitedInConfig = configuredFeatures.indexOf(AppConstants.FEATURE_LimitedListPersonalization) !== -1;

      const actionRefs = [];

      if (this.includeSearch(listSpec, listStateInfo) && !moreActionsMenu) {
        actionRefs.push(this.getStandardOptionsMenuItems().search);
      }

      if (!disallowExport && noPdfInConfig) {
        actionRefs.push(this.getStandardOptionsMenuItems().printToFile);
      }

      if (noCsvInConfig && listStateInfo.dataViewSet) {
        actionRefs.push(this.getStandardOptionsMenuItems().exportToCSV);
      }

      if (noCreateReportInConfig) {
        actionRefs.push(this.getStandardOptionsMenuItems().createReport);
      }

      actionRefs.push(this.getStandardOptionsMenuItems().relatedReport);

      if (noConfigInConfig || noPersonlizationInConfig || noLimitedInConfig) {
        actionRefs.push(this.getStandardOptionsMenuItems().personalize);
      }
      if (listStateInfo.selectedDataView
        && !listStateInfo.selectedDataView.isNewRow
        && listSpec.busClassSpec
        && (listSpec.busClassSpec.implAuditing || listSpec.busClassSpec.implEffectiveDated)
        && !(this.lmListSpecUtilService.isColumnarView(listSpec) && listStateInfo.displayAs === 'chart')
      ) {
        if (!listStateInfo.selectedDataView.viewAuditSecured) {
          const auditLogRef: LMBusActionReference = this.getStandardOptionsMenuItems().auditLog;
          auditLogRef.label = this.lmFormatUtilService.formatMessage(auditLogRef.label, listSpec.busClassSpec.name);
          actionRefs.push(auditLogRef);
        }

        if (!listStateInfo.selectedDataView.viewFullAuditSecured) {
          const fullAuditLogRef: LMBusActionReference = this.getStandardOptionsMenuItems().fullAuditLog;
          fullAuditLogRef.label = this.lmFormatUtilService.formatMessage(fullAuditLogRef.label, listSpec.busClassSpec.name);
          actionRefs.push(this.getStandardOptionsMenuItems().fullAuditLog);
        }
      }

      if (listSpec.busListSpec.implementsAuditCompare
          && !(this.lmListSpecUtilService.isColumnarView(listSpec) && listStateInfo.displayAs === 'chart')) {
        actionRefs.push(this.getStandardOptionsMenuItems().auditCompare);

      }

      return actionRefs;
    } catch (e) {
      this.lmLoggerService.error(e);
    }
  }

  private buildCalledOutToolbarButtons(listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    const isCardViewWithDetailSection = this.lmListSpecUtilService.isCardViewWithDetailSections(listSpec);
    const calledOutActionRefs: LMBusActionReference[] = this.getCalledOutToolbarActionRefs(listSpec, listStateInfo);

    let calledOutToolbarButtons: Array<LMToolbarButton> = this.actionRefArrayToToolbarButtonArray(listSpec, calledOutActionRefs);

    if (isCardViewWithDetailSection) {
      // Allow one button to be outside the overflow menu when a card view with details section
      calledOutToolbarButtons.filter((button, index) => index > 0).forEach(button => button.isMoreMenuItem = true);

      // -----------------------------------------------------------------------------
      // build any secondary toolbar based on the secondaryToolbarButtonIds array
      // -----------------------------------------------------------------------------
      this.buildSecondaryToolbarButtons(listSpec, listStateInfo);
    }

    if (!this.lmContextService.casualMode() && !listStateInfo.calledOutActionsOnly) {
      const secondaryActionRefs: Array<LMBusActionReference> = this.getSecondaryToolbarActionReferences(listSpec, listStateInfo);
      const excludeActionRefs = [ ...calledOutActionRefs, ...secondaryActionRefs ];
      const includeCreateActions = !this.hasCreateMenu(listSpec.busListSpec.toolbar);
      const moreMenuItems = this.getAllActionsMenuItems(listSpec, listStateInfo, excludeActionRefs, includeCreateActions);
      calledOutToolbarButtons = [ ... calledOutToolbarButtons, ... moreMenuItems ];
    }

    this.toolbarButtons = [ ...this.toolbarButtons, ...calledOutToolbarButtons ];
  }

  private getCalledOutToolbarActionRefs(listSpec: LMListSpec, listStateInfo: LMListStateInfo): LMBusActionReference[] {
    return listSpec.busListSpec.toolbar
      .filter(actionRef => {
        let includeAction = this.includeCalledOutActionRef(actionRef, listSpec, listStateInfo);
        if (includeAction && listStateInfo.isSelectListInSelectionMode) {
          includeAction = this.includeSelectionModeCalledOutActionRefs(actionRef, listSpec, listStateInfo);
        }
        return includeAction;
      })
      .map(actionRef =>
        this.mapCalledOutActionRef(listSpec, actionRef, listStateInfo));
  }

  private includeCalledOutActionRef(actionRef: LMBusActionReference, listSpec: LMListSpec, listStateInfo: LMListStateInfo): boolean {
    // -----------------------------------------------------------------------------
    // sort button is only allowed on a card view or on a list grid containing
    // a card view that has sort orders
    // -----------------------------------------------------------------------------
    if (actionRef.id === AppConstants.STANDARD_TOOLBAR_SORT) {
      return ((this.lmListSpecUtilService.isListViewWithSorts(listSpec)
        || this.lmListSpecUtilService.isGridWithCardViewSorts(listSpec))
        && !!listStateInfo.dataViewSet
        && !!listStateInfo.dataViewSet?.pagingInfo);
    }
    if (actionRef.id === AppConstants.STANDARD_TOOLBAR_SAVEDSEARCHES) {
      return this.showUserListViewToolbarButton(listSpec, listStateInfo);
    }

    // -----------------------------------------------------------------------------
    // exclude any secondary toolbar buttons from the primary toolbar
    // -----------------------------------------------------------------------------
    if (this.isSecondaryToolbarButton(actionRef.id)) {
      return false;
    }

    if (listSpec.showAsMingleWidget) {
      if (actionRef.type !== 'standard') {
        return false;
      }

      if (actionRef.id === AppConstants.STANDARD_TOOLBAR_SEARCH) {
        // -----------------------------------------------------------------------------
        // If this listSpec has a card view only (i.e. no other display fields) then
        // don't allow a search button. Keyword search is allowed and it handled
        // already by lm-list.component.
        // -----------------------------------------------------------------------------
        if (this.lmListSpecUtilService.isCardViewOnly(listSpec)) {
          return false;
        }

        // -----------------------------------------------------------------------------
        // allow normal datagrids to use the filter bar when in a mingle widget
        // -----------------------------------------------------------------------------
        return true;
      } else {
        return false;
      }
    }
    return true;
  }

  private includeSelectionModeCalledOutActionRefs(actionRef: LMBusActionReference, listSpec: LMListSpec, listStateInfo: LMListStateInfo): boolean {
    if (actionRef.id === AppConstants.STANDARD_TOOLBAR_REFRESH
        || actionRef.id === AppConstants.STANDARD_TOOLBAR_SEARCH
        || actionRef.id === AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON) {
      return true;
    }

    if (actionRef.id === AppConstants.STANDARD_TOOLBAR_SORT) {
      return (this.lmListSpecUtilService.isListViewWithSorts(listSpec) && !!listStateInfo.dataViewSet && !!listStateInfo.dataViewSet.pagingInfo);
    }

    const actionSpec: LMActionSpec = listSpec.busClassSpec.actions[ actionRef.index ];
    return (actionSpec?.type === AppConstants.ACTION_TYPE_CREATE);
  }

  private mapCalledOutActionRef(listSpec: LMListSpec, actionRef: LMBusActionReference, listStateInfo: LMListStateInfo): LMBusActionReference {

    if (!actionRef.label && actionRef.labelMessageRule) {
      const dataView = listStateInfo.selectedDataView ? listStateInfo.selectedDataView : listStateInfo.dataViewSet.data[ 0 ];
      actionRef.label = this.lmFormatUtilService.formatMessageRule(actionRef.labelMessageRule, dataView );
    }

    // filter out the sort button if it's used anywhere other than a list-view
    if (actionRef.id === AppConstants.STANDARD_TOOLBAR_SORT) {
      return this.buildSortActionRef(actionRef, listStateInfo);
    }
    if (actionRef.id === AppConstants.STANDARD_TOOLBAR_SAVEDSEARCHES) {
      const userListView = this.getStandardToolbarActions().userListViewMenu;
      userListView.label = actionRef.label;
      return userListView;
    }
    if (actionRef.id === AppConstants.STANDARD_TOOLBAR_HELPERLIST && listSpec.busListSpec.helperLists && listSpec.busListSpec.helperLists.length > 1) {
      return {
        id: AppConstants.STANDARD_TOOLBAR_HELPERLISTMENU_BUTTON,
        automationId: AppConstants.STANDARD_TOOLBAR_HELPERLISTMENU_BUTTON,
        type: 'standard',
        tooltip: Soho.Locale.translate('HelperList'),
        icon: 'amend'
      };
    }

    if (actionRef.id === AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON) {

      const actionSpecArray: Array<LMActionSpec> = this.lmActionService.getCreateActions(
        listSpec.busClassSpec.actions, listStateInfo.dataViewSet.header.actionIndexes);

      if (listStateInfo.inlineCreateEnabled && listSpec.busListSpec.allowFormCreate) {
        const inlineCreateAction: LMActionSpec = { name: AppConstants.STANDARD_TOOLBAR_INLINECREATE, type: 'CREATE' };
        actionSpecArray.push(inlineCreateAction);
      }

      // if only one valid create action, display that instead of the called out create menu
      if (actionSpecArray && actionSpecArray.length === 1) {
        const createActionRefs: Array<LMBusActionReference> = this.actionSpecsToActionRefs(actionSpecArray);
        const createRef = createActionRefs[0];

        // If there is no label on called out, show no label
        if (!actionRef.label) {
          createRef.label = undefined;
        }

        // if single create doesn't have a icon, use the create menu icon
        if (!createRef.icon) {
          createRef.icon = actionRef.icon;
        }

        return createRef;
      }
    }

    return actionRef;
  }

  private buildProcessFlowActionToolbarButtons(listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    if (this.lmContextService.getContext().configuredFeatures.indexOf(AppConstants.FEATURE_ShowInbasketWorkFlowActions) === -1
      || this.lmContextService.hasAsOfDateInPast()) {
      return;
    }

    if (listStateInfo.selectedRows.length > 1) {
      const dataViews: LMDataView[] = [];
      listStateInfo.selectedRows.forEach((selectedRow) => {
        dataViews.push(listStateInfo.dataViewSet.data[selectedRow]);
      });
      const allCommonPFActions = this.lmActionService.getValidProcessFlowActions(dataViews);
      const pfActionRefs = this.pfActionsToActionRefArray(allCommonPFActions);
      const pfActionToolbarBttons: Array<LMToolbarButton> = this.actionRefArrayToToolbarButtonArray(listSpec, pfActionRefs);
      this.toolbarButtons = [ ...this.toolbarButtons, ...pfActionToolbarBttons ];
      return;
    }

    if (listStateInfo.selectedDataView && listStateInfo.selectedDataView.processFlowActions && listStateInfo.selectedDataView.processFlowActions.length > 0) {
      const pfActionRefs = this.pfActionsToActionRefArray(listStateInfo.selectedDataView.processFlowActions);
      const pfActionToolbarButtons: Array<LMToolbarButton> = this.actionRefArrayToToolbarButtonArray(listSpec, pfActionRefs);

      this.toolbarButtons = [ ...this.toolbarButtons, ...pfActionToolbarButtons ];
    }
  }

  private buildStandardToolbarButtons(listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    const isCardViewWithDetailSections = this.lmListSpecUtilService.isCardViewWithDetailSections(listSpec);
    let standardActionRefs: Array<LMBusActionReference> = this.getStandardToolbarActionReferences(listSpec, listStateInfo);
    let standardToolbarButtons: Array<LMToolbarButton> = this.actionRefArrayToToolbarButtonArray(listSpec, standardActionRefs, isCardViewWithDetailSections, listStateInfo);

    const deleteActionRefs = this.lmActionService.getValidListDeleteActions(listSpec.busClassSpec.actions, listStateInfo.dataViewSet, listStateInfo.selectedRows);

    let exludedActionRefs = standardActionRefs;
    if (this.lmListSpecUtilService.isCardViewWithDetailSections(listSpec)) {
      // -----------------------------------------------------------------------------
      // move all buttons for a cardList with detail section into the overflow or
      // more menu.
      // -----------------------------------------------------------------------------
      standardToolbarButtons.forEach(toolbarButton => toolbarButton.isMoreMenuItem = true);

      // -----------------------------------------------------------------------------
      // filter out standard delete when a delete action exists on the business class
      // -----------------------------------------------------------------------------
      if (deleteActionRefs.length > 0) {
        standardToolbarButtons = standardToolbarButtons.filter(button => !(button.actionReference && button.actionReference.id === AppConstants.STANDARD_TOOLBAR_DELETE));
        standardActionRefs = standardActionRefs.filter(action => !(action.id === AppConstants.STANDARD_TOOLBAR_DELETE));
      }

      // -----------------------------------------------------------------------------
      // build any secondary toolbar based on the secondaryToolbarButtonIds array
      // -----------------------------------------------------------------------------
      const secondaryActionRefs: Array<LMBusActionReference> = this.getSecondaryToolbarActionReferences(listSpec, listStateInfo);
      this.secondaryToolbarButtons = this.actionRefArrayToToolbarButtonArray(listSpec, secondaryActionRefs);

      exludedActionRefs = [ ...standardActionRefs, ...secondaryActionRefs ];
      const userListViewMenuButton = standardToolbarButtons.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_MENU);
      if (userListViewMenuButton) {
        const userListView: Array<LMBusActionReference> = [ { id: 'noop', type: 'noop', label: 'Not yet implemented' } ];
        userListViewMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, userListView);
      }
    } else {
      if (!this.lmActionService.hasStandardDelete(deleteActionRefs)) {
        exludedActionRefs = exludedActionRefs.filter(action => !(action.id === AppConstants.STANDARD_TOOLBAR_DELETE));
      }
    }

    if (listStateInfo.suppressStandardToolbar) {
      // if suppress standard toolbar, move all buttons into the more menu
      standardToolbarButtons.forEach(toolbarButton => toolbarButton.isMoreMenuItem = true);
      // If UserListView is in the moreMenuItem, then it requires menu to be set.
      const userListViewMenuButton = standardToolbarButtons.find(button => button.actionReference.id === AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_MENU);
      if (userListViewMenuButton) {
        const userListView: Array<LMBusActionReference> = [ { id: 'noop', type: 'noop', label: 'Not yet implemented' } ];
        userListViewMenuButton.menu = this.createToolbarMenuItemsFromActionReferences(listSpec, userListView);
      }
    }

    const moreMenuItems: Array<LMToolbarButton> = this.getAllActionsMenuItems(listSpec, listStateInfo, exludedActionRefs, false);
    standardToolbarButtons = [ ...standardToolbarButtons, ...moreMenuItems ];
    this.toolbarButtons = [ ...this.toolbarButtons, ...standardToolbarButtons ];
  }

  private buildGalleryToolbarButtons(listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    const actionReferences: Array<LMBusActionReference> = [ this.getGalleryToolbarActions().prevRecord, this.getGalleryToolbarActions().nextRecord, this.getGalleryToolbarActions().viewAll ];
    const galleryToolbarButtons: Array<LMToolbarButton> = this.actionRefArrayToToolbarButtonArray(listSpec, actionReferences, false, listStateInfo);
    const isMultiListOrMultiPane = this.lmListSpecUtilService.isMultiListPanelContent(listSpec) || this.lmListSpecUtilService.isMultiPanePanelContent(listSpec);

    this.toolbarButtons = galleryToolbarButtons.map((button) => {
      if (button.actionReference.type === 'viewall') {
        button.displayAsLink = true;
      }

      button.state = { disabled: false, visible: listSpec.busListSpec.allowHorizontalPaging || isMultiListOrMultiPane };
      return button;
    });
  }

  private buildSecondaryToolbarButtons(listSpec: LMListSpec, listStateInfo: LMListStateInfo) {
    const secondaryActionRefs: Array<LMBusActionReference> = this.getSecondaryToolbarActionReferences(listSpec, listStateInfo);
    this.secondaryToolbarButtons = this.actionRefArrayToToolbarButtonArray(listSpec, secondaryActionRefs);
  }

  private getSecondaryToolbarActionReferences(listSpec: LMListSpec, listStateInfo: LMListStateInfo): Array<LMBusActionReference> {
    const allowUpdate = this.lmListSpecUtilService.allowUpdate(listSpec);

    const actionReferenceArray: Array<LMBusActionReference> = [];

    // --------------------------------------------------------------------
    // SAVE
    // --------------------------------------------------------------------
    if (allowUpdate && this.isSecondaryToolbarButton(AppConstants.STANDARD_SAVE)) {
      actionReferenceArray.push(this.getStandardToolbarActions().save);
    }

    // --------------------------------------------------------------------
    // REFRESH
    // --------------------------------------------------------------------
    if (this.isSecondaryToolbarButton(AppConstants.STANDARD_TOOLBAR_REFRESH)) {
      actionReferenceArray.push(this.getStandardToolbarActions().refresh);
    }

    return actionReferenceArray;
  }

  private isSecondaryToolbarButton(toolbarButtonId: string): boolean {
    return this.secondaryToolbarButtonIds.some(buttonId => buttonId === toolbarButtonId);
  }

  private getStandardToolbarActionReferences(listSpec: LMListSpec, listStateInfo: LMListStateInfo): Array<LMBusActionReference> {
    const helperLists = this.getHelperLists(listSpec);
    const isUpdatable = listStateInfo.isUpdatable;
    const openActionRestricted = this.getOpenActionRestricted(listSpec);
    const includeChartMenu = this.getIncludeChartMenu(listSpec);
    const includeSaveAndNew = this.getIncludeSaveAndNew(listStateInfo);

    const actionReferenceArray: Array<LMBusActionReference> = [];

    // --------------------------------------------------------------------
    // SEARCH FOR MINGLE?!?
    // --------------------------------------------------------------------
    const includeSearch = this.includeSearch(listSpec, listStateInfo);
    if (includeSearch && listSpec.showAsMingleWidget) {
      actionReferenceArray.push(this.getStandardToolbarActions().search);
      return actionReferenceArray;
    }

    // --------------------------------------------------------------------
    // CREATE
    // --------------------------------------------------------------------
    const actionSpecArray: Array<LMActionSpec> = this.lmActionService.getCreateActions(
      listSpec.busClassSpec.actions, listStateInfo.dataViewSet?.header.actionIndexes);

    if (listStateInfo.inlineCreateEnabled && listSpec.busListSpec.allowFormCreate) {
      const inlineCreateAction: LMActionSpec = { name: AppConstants.STANDARD_TOOLBAR_INLINECREATE, type: 'CREATE' };
      actionSpecArray.push(inlineCreateAction);
    }

    if (actionSpecArray && actionSpecArray.length === 1) {
      actionReferenceArray.push(this.getStandardToolbarActions().create);
    } else if (actionSpecArray && actionSpecArray.length > 1) {
      actionReferenceArray.push(this.getStandardToolbarActions().createMenu);
    }

    // TODO tpatton remove restriction and add drill into.
    // --------------------------------------------------------------------
    // OPEN
    // The open action runs the first valid update action
    // If there is no valid update action, then the form is opened
    // using the 'formIs..." declaration or the primary form in a read-only state
    // --------------------------------------------------------------------
    if (!openActionRestricted
        &&  !(this.lmListSpecUtilService.isColumnarView(listSpec) && listStateInfo.displayAs === 'chart')) {
      actionReferenceArray.push(this.getStandardToolbarActions().open);
    }

    // --------------------------------------------------------------------
    // SAVE
    // --------------------------------------------------------------------
    // dependent lists save functionality will be handle by the
    // controlling list or form.
    // ------------------------------------------------------------
    if (isUpdatable && !listSpec.dependentList) {
      const saveAction = this.getStandardToolbarActions().save;
      if (listStateInfo.isRootComponent && !this.isSecondaryToolbarButton(saveAction.id)) {
        // --------------------------------------------------------------------
        // make sure it's not in the secondary toolbar before adding to the
        // primary toolbar
        // --------------------------------------------------------------------
        actionReferenceArray.push(saveAction);
      }
    }

    // --------------------------------------------------------------------
    // SAVE-AND-NEW
    // --------------------------------------------------------------------
    if (includeSaveAndNew) {
      actionReferenceArray.push(this.getStandardToolbarActions().saveAndNew);
    }

    // --------------------------------------------------------------------
    // SORT (for card views)
    // ------------------------------------------------------------

    // Sort actionRef. Only add if dataViewSet is available.
    // dataViewSet.pagingInfo.isAscending is needed to set the
    // icon and tooltip
    // ------------------------------------------------------------
    if (this.lmListSpecUtilService.isListViewWithSorts(listSpec) && listStateInfo.dataViewSet && listStateInfo.dataViewSet.pagingInfo) {
      actionReferenceArray.push(this.buildSortActionRef(this.getStandardToolbarActions().sort, listStateInfo));
    }

    // --------------------------------------------------------------------
    // DELETE
    // ------------------------------------------------------------
    if (this.lmActionService.hasNonRestrictedAction(listSpec.busClassSpec.actions, 'DELETE')
        && !this.lmListSpecUtilService.isColumnarView(listSpec)) {
      actionReferenceArray.push(this.getStandardToolbarActions().delete);
    }

    // --------------------------------------------------------------------
    // FILTER
    // ------------------------------------------------------------
    if (includeSearch) {
      actionReferenceArray.push(this.getStandardToolbarActions().search);
    }

    // --------------------------------------------------------------------
    // UserListView
    // ------------------------------------------------------------
    if (this.showUserListViewToolbarButton(listSpec, listStateInfo)) {
      actionReferenceArray.push(this.getStandardToolbarActions().userListViewMenu);
    }

    // ------------------------------------------------------------
    // dependent lists refresh functionality will be handle by the
    // controlling list or form.
    // --------------------------------------------------------------------
    // REFRESH
    // ------------------------------------------------------------
    if ((listStateInfo.isRootComponent || listSpec.listType === 'select') && !listSpec.dependentList && !this.isSecondaryToolbarButton(AppConstants.STANDARD_TOOLBAR_REFRESH)) {
      // --------------------------------------------------------------------
      // make sure it's not in the secondary toolbar before adding to the
      // primary toolbar
      // --------------------------------------------------------------------
      actionReferenceArray.push(this.getStandardToolbarActions().refresh);
    }

    // --------------------------------------------------------------------
    // HELPERLIST
    // ------------------------------------------------------------
    if (helperLists) {
      if (helperLists.length > 1) {
        actionReferenceArray.push({
          id: AppConstants.STANDARD_TOOLBAR_HELPERLISTMENU_BUTTON,
          automationId: AppConstants.STANDARD_TOOLBAR_HELPERLISTMENU_BUTTON,
          type: 'standard',
          tooltip: Soho.Locale.translate('HelperList'),
          icon: 'amend'
        });
      } else {
        actionReferenceArray.push({
          id: AppConstants.STANDARD_TOOLBAR_HELPERLIST,
          automationId: AppConstants.STANDARD_TOOLBAR_HELPERLIST,
          type: 'standard',
          tooltip: Soho.Locale.translate('HelperList'),
          icon: 'amend'
        });
      }
    }

    // --------------------------------------------------------------------
    // CHART MENU
    // ------------------------------------------------------------
    if (includeChartMenu) {
      actionReferenceArray.push(this.getStandardToolbarActions().chartsMenu);
    }

    return actionReferenceArray;
  }

  private buildSortActionRef(sortActionRef: LMBusActionReference, listStateInfo: LMListStateInfo): LMBusActionReference {
    if (listStateInfo.dataViewSet.pagingInfo.isAscending) {
      sortActionRef.icon = 'sort-ascending';
    } else {
      sortActionRef.icon = 'sort-descending';
    }
    return sortActionRef;
  }

  private getAllValidActionReferences(
    listSpec: LMListSpec,
    listStateInfo: LMListStateInfo,
    excludedActionRefs: Array<LMBusActionReference>,
    includeCreateActions: boolean,
    noUpdateMode: boolean,
    isContextMenu?: boolean
  ): Array<LMBusActionReference> {
    let validActionRefs: Array<LMBusActionReference> = [];

    // Sort actionRef. Only add if dataViewSet is available.
    // dataViewSet.pagingInfo.isAscending is needed to set the
    // icon and tooltip
    // ------------------------------------------------------------

    if (this.lmListSpecUtilService.isListViewWithSorts(listSpec)
      && listStateInfo.dataViewSet?.pagingInfo
      && !excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_SORT)
    ) {
      validActionRefs.push(this.buildSortActionRef(this.getStandardToolbarActions().sort, listStateInfo));
    }
    // todo kris why is this at the top of the action ref list? - Phillip
    if (listStateInfo.dataViewSet?.header.auditView && listStateInfo.selectedDataView) {
      validActionRefs = validActionRefs.concat(this.getAuditViewActions());
    }

    // TODO tpatton remove restriction and add drill into.
    // --------------------------------------------------------------------
    // OPEN
    // --------------------------------------------------------------------
    if (!listSpec.busListSpec.openActionRestricted
        &&  !excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_OPEN )
        &&  !(listStateInfo.isSelectListInSelectionMode && !isContextMenu)
        &&  !(this.lmListSpecUtilService.isColumnarView(listSpec) && listStateInfo.displayAs === 'chart')) {
      validActionRefs.push(this.getStandardToolbarActions().open);
    }

    // In noUpdateMode such as when As Of Date is in the past, do not include update/instance/delete actions
    if (noUpdateMode || this.lmListSpecUtilService.isColumnarView(listSpec)) {
      return validActionRefs;
    }

    // --------------------------------------------------------------------
    // SAVE
    // add save when the list is root and it or a dependent list
    // implements inline create or allows update
    // --------------------------------------------------------------------
    if (listStateInfo.isRootComponent && !listSpec.dependentList && listStateInfo.isUpdatable) {
      if (!excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_SAVE)) {
        validActionRefs.push(this.getStandardToolbarActions().save);
      }
    }

    // --------------------------------------------------------------------
    // SAVE AND NEW
    // --------------------------------------------------------------------
    if (listStateInfo.inlineCreateEnabled) {
      validActionRefs.push(this.getStandardToolbarActions().saveAndNew);
    }

    // --------------------------------------------------------------------
    // REFRESH
    // --------------------------------------------------------------------
    if ((listStateInfo.isRootComponent || listSpec.listType === 'select') && !listSpec.dependentList && !excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_REFRESH )) {
      validActionRefs.push(this.getStandardToolbarActions().refresh);
    }

    // --------------------------------------------------------------------
    // SEARCH
    // --------------------------------------------------------------------
    if (this.includeSearch(listSpec, listStateInfo) && !excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_SEARCH )) {
      if (listSpec.busListSpec.searchForm || (!listSpec.busListSpec.searchForm && this.lmListSpecUtilService.isFilterable(listSpec, listStateInfo))) {
        validActionRefs.push(this.getStandardToolbarActions().search);
      }
    }

    if (this.showUserListViewToolbarButton(listSpec, listStateInfo) && !excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_MENU )) {
      validActionRefs.push(this.getStandardToolbarActions().userListViewMenu);
    }


    // When a select list is in selection mode only include create, refresh, and search/sort actions
    if (listStateInfo.isSelectListInSelectionMode) {
      return validActionRefs;
    }

    if (listSpec.busListSpec.helperLists?.length === 1 && !excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_HELPERLIST ) && !isContextMenu) {
      validActionRefs.push({
        id: AppConstants.STANDARD_TOOLBAR_HELPERLIST,
        automationId: AppConstants.STANDARD_TOOLBAR_HELPERLIST,
        type: 'standard',
        tooltip: Soho.Locale.translate('HelperList'),
        icon: 'amend',
        validity: listSpec.busListSpec.helperLists[ 0 ].validity
      });
    }

    let addSeparator = false;

    if (includeCreateActions) {
      // INLINE CREATE/ADD ROW
      if (listStateInfo.inlineCreateEnabled && listSpec.busListSpec.allowFormCreate) {
        validActionRefs.push(this.getStandardActionMenuItems().inlineCreate);
      }

      if (listStateInfo.createWithinEnabled) {
        validActionRefs.push(this.getStandardActionMenuItems().createWithin);
      }
      addSeparator = true;
    }

    // --------------------------------------------------------------------
    // COPY ROW
    // --------------------------------------------------------------------
    if (listStateInfo.inlineCreateEnabled) {
      validActionRefs.push(this.getStandardActionMenuItems().copyRow);
      addSeparator = true;
    }

    validActionRefs = this.filterOutExcludedActions(validActionRefs, excludedActionRefs);

    if (addSeparator && validActionRefs?.length && validActionRefs[validActionRefs.length - 1].id !== this.getStandardSeparatorMenuItem().id) {
      validActionRefs.push(this.getStandardSeparatorMenuItem());
      addSeparator = false;
    }

    const actions = listSpec.busClassSpec.actions;

    if (!listSpec.isTemplateList && includeCreateActions) {
      // -----------------------------------------------------------------------------
      // Create Action is never valid in a template list! The UI uses the update or
      // open action and dynamically switches to a create action if the template
      // target dataView is not defined
      // -----------------------------------------------------------------------------
      const createActionSpecs: Array<LMActionSpec> = this.lmActionService.getCreateActions(actions, listStateInfo.dataViewSet?.header.actionIndexes);
      const createActionRefs: Array<LMBusActionReference> = this.actionSpecsToActionRefsSorted(createActionSpecs);

      validActionRefs = validActionRefs.concat(createActionRefs);
      addSeparator = true;
    }

    // --------------------------------------------------------------------
    // IMPORT
    // --------------------------------------------------------------------
    if (listStateInfo.dataViewSet) {
      const importActionRefs = this.actionSpecsToActionRefs(this.lmActionService.getValidListImportActions(actions, listStateInfo.dataViewSet));
      validActionRefs = validActionRefs.concat(importActionRefs);
      if (importActionRefs && importActionRefs.length > 0) {
        addSeparator = true;
      }
    }

    validActionRefs = this.filterOutExcludedActions(validActionRefs, excludedActionRefs);

    if (addSeparator && (validActionRefs && validActionRefs.length > 0) && validActionRefs[validActionRefs.length - 1].id !== this.getStandardSeparatorMenuItem().id) {
      validActionRefs.push(this.getStandardSeparatorMenuItem());
    }

    // Sort updateActions, instanceActions, setActions by label
    let updateActionRefs = [];
    let setActionRefs = [];

    const multipleRowsSelected = !listStateInfo.selectedRows ? false : listStateInfo.selectedRows.length > 1;

    if (!multipleRowsSelected && !listSpec.busListSpec.openActionRestricted ) {
      updateActionRefs = this.actionSpecsToActionRefs(this.lmActionService.getValidListUpdateActions(actions, listStateInfo.dataViewSet, listStateInfo.selectedRows), listStateInfo.selectedDataView);
    }

    // set actions DO NOT run against a list record
    setActionRefs = this.actionSpecsToActionRefs(this.lmActionService.getValidListSetActions(actions, listStateInfo.dataViewSet));

    const instanceActionRefs = this.actionSpecsToActionRefs(this.lmActionService.getValidListInstanceActions(actions, listStateInfo.dataViewSet, listStateInfo.selectedRows));

    let a = [];

    // Do NOT show the update action unless there are multiple,
    // If there is zero or 1 update action then the UI should only display the
    // open action
    // if (updateActionRefs && updateActionRefs.length > 1) {
    a = a.concat(updateActionRefs);
    // }

    a = a.concat(instanceActionRefs);
    a = a.concat(setActionRefs);
    a = _.sortBy(a, 'label');

    validActionRefs = validActionRefs.concat(a);

    const deleteActionRefs = this.actionSpecsToActionRefs(this.lmActionService.getValidListDeleteActions(actions, listStateInfo.dataViewSet, listStateInfo.selectedRows));
    // is the standard delete on the toolbar?
    if (excludedActionRefs.some(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_DELETE)) {
      // do not display a single delete action in the More menu
      if (deleteActionRefs.length > 1) {
        validActionRefs = validActionRefs.concat(deleteActionRefs);
      }
    } else {
      validActionRefs = validActionRefs.concat(deleteActionRefs);
    }

    return this.filterOutExcludedActions(validActionRefs, excludedActionRefs);
  }

  private buildSelectListCasualModeToolbarButtons(listSpec: LMListSpec): void {
    const actionReferenceArray: Array<LMBusActionReference> = [];

    if (!listSpec.busListSpec.keywordSearch) {
      actionReferenceArray.push(this.getStandardToolbarActions().search);
    }

    actionReferenceArray.push(this.getStandardToolbarActions().refresh);
    const selectListButtons = this.actionRefArrayToToolbarButtonArray(listSpec, actionReferenceArray);
    this.toolbarButtons = [ ...this.toolbarButtons, ...selectListButtons ];
  }

  private buildSelectListToolbarButtons(listSpec: LMListSpec, listStateInfo: LMListStateInfo): void {
    if (listSpec.busListSpec.toolbar) {
      this.buildCalledOutToolbarButtons(listSpec, listStateInfo);
      return;
    }

    const actionReferenceArray: Array<LMBusActionReference> = [];

    actionReferenceArray.push(this.getStandardToolbarActions().search);
    actionReferenceArray.push(this.getStandardToolbarActions().refresh);
    let selectListButtons = this.actionRefArrayToToolbarButtonArray(listSpec, actionReferenceArray);

    const excludedActionRefs = actionReferenceArray;
    excludedActionRefs.push(this.getStandardToolbarActions().open);

    const moreMenuItems: Array<LMToolbarButton> = this.getAllActionsMenuItems(listSpec, listStateInfo, excludedActionRefs, false);
    selectListButtons = [ ... selectListButtons, ... moreMenuItems ];
    this.toolbarButtons = [ ...this.toolbarButtons, ...selectListButtons ];
  }

  private getChartMenuActionRefs(listSpec: LMListSpec, listStateInfo: LMListStateInfo): Array<LMBusActionReference> {
    const chartMenuOptions = this.getChartMenuOptions(listSpec);
    const chartMenuItem: Array<LMBusActionReference> = [];
    const listTitle = listSpec.busListSpec.title;
    const header = listStateInfo.dataViewSet?.header;

    if (!listSpec.busListSpec.showChartsOnly &&
        listSpec.listType !== 'cubeview' &&
        (!listSpec.busListSpec.implementsTreeView || !listSpec.busListSpec.implementsOrgChartView)
    ) {
      chartMenuItem.push({
        id:    'list',
        type:  'list',
        isChecked: this.lmListSpecUtilService.isColumnarView(listSpec) && listStateInfo?.chartName === undefined,
        label: this.lmFormatUtilService.formatMessage(Soho.Locale.translate('DefaultList'), this.lmFormatUtilService.formatMessageRule(listTitle, header))
      });
    }

    if (listSpec.listType !== 'cubeview' &&
      listSpec.busListSpec.implementsTreeView &&
      !listSpec.busListSpec.implementsOrgChartView
    ) {
      chartMenuItem.push({
        id:    'tree',
        type:  'tree',
        label: this.lmFormatUtilService.formatMessage(Soho.Locale.translate('TreeList'), this.lmFormatUtilService.formatMessageRule(listTitle, header))
      });
    }

    if (listSpec.busListSpec.implementsOrgChartView &&
      listSpec.busListSpec.displayFields &&
      listSpec.busListSpec.displayFields.length > 0
    ) {
      chartMenuItem.push({
        id:    'hierarchy',
        type:  'hierarchy',
        label: this.lmFormatUtilService.formatMessage(Soho.Locale.translate('HierarchyList'), this.lmFormatUtilService.formatMessageRule(listTitle, header))
      });
    }

    if (listSpec.busListSpec.implementsGalleryView &&
      listSpec.busListSpec.displayFields &&
      listSpec.busListSpec.displayFields.length > 0
    ) {
      chartMenuItem.push({
        id:    'gallery',
        type:  'gallery',
        label: this.lmFormatUtilService.formatMessage(Soho.Locale.translate('GalleryView'), this.lmFormatUtilService.formatMessageRule(listTitle, header))
      });
    }

    if (chartMenuOptions) {
      if (chartMenuItem.length > 0) {
        chartMenuItem.push(this.getStandardSeparatorMenuItem());
      }

      for (const key in chartMenuOptions) {
        if (!chartMenuOptions.hasOwnProperty((key))) {
          continue;
        }

        const chart = chartMenuOptions[ key ];
        let label = '';
        if (chart.title) {
          label = this.lmFormatUtilService.formatMessageRule(chart.title, header);
        }

        let chartMenu: LMBusActionReference;
        if (listSpec.listType === 'cubeview') {
          chartMenu = {
            id: AppConstants.STANDARD_TOOLBAR_CHARTMENU_BUTTON,
            automationId: AppConstants.STANDARD_TOOLBAR_CHARTMENU_BUTTON,
            type: 'cube',
            name: chart.id,
            label,
          };
          if (listStateInfo.cubeDisplayChartName
              && listStateInfo.cubeDisplayChartName === chart.id) {
            chartMenu.isChecked = true;
          }
        } else {
          chartMenu = {
            id:    chart.id,
            type:  listSpec.busListSpec.charts.Calendar ? 'calendar' : 'chart',
            label,
          };
          if (this.lmListSpecUtilService.isColumnarView(listSpec)
              && listStateInfo?.chartName === chart.id) {
            chartMenu.isChecked = true;
          }
        }
        chartMenuItem.push(chartMenu);

      }
    }

    return chartMenuItem;
  }

  protected includeActionRef(spec: any, actionReference: LMBusActionReference, listStateInfo?: LMListStateInfo): boolean {
    const listSpec: LMListSpec = <LMListSpec> spec;
    if (actionReference.id === AppConstants.STANDARD_TOOLBAR_SEARCH) {
      return this.includeSearch(listSpec, listStateInfo);
    }
    return true;
  }

  private includeSearch(listSpec: LMListSpec, listStateInfo?: LMListStateInfo): boolean {

    /**
     * Search form display
     * default = The search form is closed on initial load but can be toggled open.
     * always = The search form will always display no toggle is needed.
     * initial = The search form will display on initial load but can be toggled.
     */

    const searchForm = listSpec.busListSpec.searchForm;
    const displaySearchAlways = searchForm ? searchForm.alwaysDisplaySearch : false;
    const displaySearchDefault = listSpec.busListSpec.displaySearch !== 'default' && listSpec.busListSpec.displaySearch !== 'initially';
    const isCardStack = this.lmListSpecUtilService.isCardViewOnly(listSpec);
    const useSearchAsFilter = searchForm ? listSpec.busListSpec.searchForm.useSearchAsFilter : false;
    let isChart = false;

    if (listSpec.busListSpec && listSpec.busListSpec.charts) {
      if (listSpec.busListSpec.defaultChartName) {
        isChart = true;
      } else {
        const listResource = new ListResource({ resourceId: listSpec.resourceId });
        if (listResource.chartName) {
          isChart = true;
        }
      }

      if (listStateInfo && (listStateInfo.displayAs !== 'chart' && listStateInfo.displayAs !== 'calendar')) {
        isChart = false;
      }
    }

    // Don't show filters in the case where the list is a chart
    // LMCLIENT-27998 except if a search form is configured
    if (isChart && !searchForm) {
      return false;
    }

    if (isCardStack) {

      if (displaySearchAlways) {
        // ----------------------------------------------------------------------------
        // if display always then no need to display a search button
        // ----------------------------------------------------------------------------
        return false;
      }

      return !!searchForm;
    }

    if (displaySearchAlways) {
      return false;
    }

    // if (listSpec.busListSpec.implementsCubeView /*&& m_searchPanel == USING_FILTER_COLUMN_PANEL*/) { // Impl the cmt
    //   return false;
    // }

    // if both 'use as filter' and 'always display' are false and search form is not default -
    // then search form will always display, don't show toggle button
    if (!displaySearchAlways && !useSearchAsFilter && displaySearchDefault) {
      return false;
    }

    return searchForm ? true : listSpec.busListSpec.implementsCubeView ? false : this.lmListSpecUtilService.isFilterable(listSpec, listStateInfo);
  }

  private showUserListViewToolbarButton(listSpec: LMListSpec, listStateInfo: LMListStateInfo): boolean {
    if (this.lmContextService.showUserListView()) {
      // TODO: What if the list has no search form or filtrable column. It can have state related to sort to be persisted in UserListView.
      // TODO: Cubes may have no search form, but other information to be persisted.

      // Show filter icon in toolbar? (search form or a filtrable column)
      if (this.includeSearch(listSpec, listStateInfo)) {
        return  true;
      }

      // Has search form and display search always or use search as filter
      const searchForm = listSpec.busListSpec.searchForm;
      const displaySearchAlways = searchForm ? searchForm.alwaysDisplaySearch : false;
      const useSearchAsFilter = searchForm ? listSpec.busListSpec.searchForm.useSearchAsFilter : false;
      if (displaySearchAlways || useSearchAsFilter) {
        return true;
      }

      // Filter bar is always open, so filter toolbar button is not rendered
      const displaySearchDefault = listSpec.busListSpec.displaySearch !== 'default' && listSpec.busListSpec.displaySearch !== 'initially';
      if (displaySearchDefault) {
        return true;
      }

      // Has toolbar search field?
      if (this.getToolbarSearchField(listSpec) !== null) {
        return true;
      }

      // Child hierarchy dimension adds a default dimension filter even if no search form is defined.
      // But cube can also have other overrides in place such as dimension, period, column view,
      // pivot view or period view that effects the aggregate data.
      // Allow user to save any of the overrides, even if search is not available.
      if (listSpec.busListSpec.implementsCubeView/* && listSpec.busListSpec.hasChildHierarchyDims */) {
        return true;
      }
    }
    return false;
  }

  private standardActionState(listSpec: LMListSpec, listStateInfo: LMListStateInfo, toolbarButton: LMToolbarButton): void {
    let action: string;

    const noRowSelected = !listStateInfo.selectedRows || listStateInfo.selectedRows.length === 0 ||
        listStateInfo.selectedRows[0] === -1 || listStateInfo.dataViewSet.data.length === 0 || listStateInfo.selectedRows[0] === undefined;
    const multipleRowsSelected = !listStateInfo.selectedRows ? false : listStateInfo.selectedRows.length > 1;
    const selectedRowIndex: number = !listStateInfo.selectedRows ? -1 : listStateInfo.selectedRows[ 0 ];
    const isUpdateable = listStateInfo.isUpdatable || listStateInfo.inlineCreateEnabled;

    if (toolbarButton.actionReference) {
      action = toolbarButton.actionReference.id ? toolbarButton.actionReference.id : toolbarButton.actionReference.label.replace(/\s/g, '');
    } else if (toolbarButton.actionSpec) {
      action = toolbarButton.actionSpec.type;
    }

    let valid = false;
    let visible = false;

    /**
     *  Disable and hide button if there is no dataset or row selected
     *  But not if the button is create, search, options menu, charts menu, or refresh
     */
    if (!listStateInfo.dataViewSet
        || (noRowSelected
            && !isUpdateable
            && !listSpec.busListSpec.implementsCubeView
            && (
              action !== AppConstants.STANDARD_TOOLBAR_OPTIONSMENU_BUTTON &&
                action !== AppConstants.STANDARD_TOOLBAR_PRINTTOFILE &&
                action !== AppConstants.STANDARD_TOOLBAR_EXPORTPDF &&
                action !== AppConstants.STANDARD_TOOLBAR_EXPORTCSV &&
                action !== AppConstants.STANDARD_TOOLBAR_PERSONALIZE &&
                action !== AppConstants.STANDARD_TOOLBAR_VIEWAUDIT &&
                action !== AppConstants.STANDARD_TOOLBAR_VIEWFULLAUDIT &&
                action !== AppConstants.STANDARD_TOOLBAR_CREATEREPORT &&
                action !== AppConstants.STANDARD_TOOLBAR_RELATEDREPORT &&
                action !== AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON &&
                action !== AppConstants.STANDARD_TOOLBAR_CREATE &&
                action !== AppConstants.STANDARD_TOOLBAR_CHARTMENU_BUTTON &&
                action !== AppConstants.STANDARD_TOOLBAR_REFRESH &&
                action !== AppConstants.STANDARD_TOOLBAR_SEARCH &&
                action !== AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_SAVE &&
                action !== AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_SAVEAS &&
                action !== AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_CLEAR &&
                action !== AppConstants.STANDARD_TOOLBAR_USERLISTVIEW &&
                action !== AppConstants.STANDARD_TOOLBAR_HELPERLIST &&
                action !== AppConstants.STANDARD_TOOLBAR_HELPERLISTMENU_BUTTON &&
                action !== AppConstants.STANDARD_TOOLBAR_EDITMODE &&
                action !== AppConstants.STANDARD_TOOLBAR_SORT &&
                action !== AppConstants.STANDARD_TOOLBAR_SORT_ORDER_MENUITEM &&
                action !== AppConstants.STANDARD_TOOLBAR_SORT_ORDER_ASCENDING &&
                action !== AppConstants.STANDARD_TOOLBAR_SORT_ORDER_DESCENDING &&
                action !== AppConstants.STANDARD_SAVE))
    ) {
      toolbarButton.state.disabled = true;
      const hideWhenInvalid = (listStateInfo.isSelectListInSelectionMode) ? true : toolbarButton?.actionReference?.hideWhenInvalid;
      toolbarButton.state.visible = !hideWhenInvalid;
      return;
    }

    switch (action) {
      case AppConstants.STANDARD_TOOLBAR_ACTIONSMENU_BUTTON:
      case AppConstants.STANDARD_TOOLBAR_ALLACTIONSMENU_BUTTON:
      case AppConstants.STANDARD_TOOLBAR_SORT:
      case AppConstants.STANDARD_TOOLBAR_SORT_ORDER_MENUITEM:
      case AppConstants.STANDARD_TOOLBAR_SORT_ORDER_ASCENDING:
      case AppConstants.STANDARD_TOOLBAR_SORT_ORDER_DESCENDING:
      case AppConstants.STANDARD_TOOLBAR_EDITMODE:
        valid = true;
        visible = true;
        break;

      case AppConstants.STANDARD_TOOLBAR_CHARTMENU_BUTTON:
        valid = true;
        visible = this.getChartMenuActionRefs(listSpec, listStateInfo).length !== 1;
        break;

      case AppConstants.STANDARD_TOOLBAR_ACTIONSMENU_MENU:
      case AppConstants.STANDARD_TOOLBAR_ALLACTIONSMENU_MENU:
      case AppConstants.STANDARD_TOOLBAR_CHARTMENU_MENU:
      case AppConstants.STANDARD_TOOLBAR_CREATEMENU_MENU:
      case AppConstants.STANDARD_TOOLBAR_OPTIONSMENU_MENU:
      case AppConstants.STANDARD_TOOLBAR_RELATEDMENU_MENU:
        break;

      case AppConstants.STANDARD_TOOLBAR_INLINECREATE:
        valid = true;
        visible = true;
        break;

      case AppConstants.STANDARD_TOOLBAR_CREATEWITHIN:
      case AppConstants.STANDARD_TOOLBAR_CREATE:
      case AppConstants.STANDARD_TOOLBAR_CREATEMENU_BUTTON:
        if (listSpec.isTemplateList) {
          // Create Action is never valid in a template list!
          // the UI uses the update or open action and dynamically
          // switches to a create action if the template target dataView is
          // not defined
          valid = false;
        } else {
          const createActionSpecs: LMActionSpec[] = this.lmActionService.getCreateActions(
            listSpec.busClassSpec.actions, listStateInfo.dataViewSet.header.actionIndexes);

          if (listStateInfo.inlineCreateEnabled && listSpec.busListSpec.allowFormCreate) {
            const inlineCreateAction: LMActionSpec = { name: AppConstants.STANDARD_TOOLBAR_INLINECREATE, type: 'CREATE' };
            createActionSpecs.push(inlineCreateAction);
          }

          valid = createActionSpecs && createActionSpecs.length > 0;

          if (valid && AppConstants.STANDARD_TOOLBAR_CREATEWITHIN === action) {
            valid = listStateInfo.createWithinEnabled;
          }

          visible = valid;
        }
        break;

      case AppConstants.STANDARD_TOOLBAR_DELETE:
        if (noRowSelected) {
          valid = false;
          // } else if (multipleRowsSelected) {
          //   // todo implement me
          //   valid = true;
        } else {
          // todo: need to handle multiple selected actions here.
          const dataView: LMDataView = listStateInfo.dataViewSet.data[ selectedRowIndex ];
          const validDeleteActionSpecs = this.lmActionService.getValidListDeleteActions(
            listSpec.busClassSpec.actions, listStateInfo.dataViewSet, listStateInfo.selectedRows);
          valid = (this.lmActionService.hasStandardDelete(validDeleteActionSpecs) || dataView.isNewRow);
        }

        visible = true;
        break;

      case AppConstants.STANDARD_TOOLBAR_DETAILFIELDS:
        // todo what is this?
        break;

      case AppConstants.STANDARD_TOOLBAR_EXPORTPDF:
      case AppConstants.STANDARD_TOOLBAR_EXPORTCSV:
        valid = !this.lmContextService.getContext().disallowExport;
        visible = !this.lmContextService.getContext().disallowExport;
        break;

      case AppConstants.STANDARD_TOOLBAR_HELPERLIST:
        valid = listSpec.busListSpec.helperLists && listSpec.busListSpec.helperLists.length === 1;

        if (valid) {
          // check if the helper list has validity or visibility conditions
          // these are defined on the panel so they must be reolved using the fromDataView
          const helperList = listSpec.busListSpec.helperLists[0];
          if (helperList.visibility) {
            valid = this.lmConditionUtilService.isValid(helperList.visibility, listStateInfo.fromDataView);
          }

          if (valid && helperList.validity) {
            valid = this.lmConditionUtilService.isValid(helperList.validity, listStateInfo.fromDataView);
          }

          // check if the called out action has a validity condition
          // this is defined on the list toolbar and is resolved with the dataViewSet
          if (valid && toolbarButton.actionReference.validity) {
            valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, listStateInfo.dataViewSet.header);
          }
          visible = true;
        } else {
          visible = false;  // why show this action if the list doesn't have any helper lists!
        }
        break;

      case AppConstants.STANDARD_TOOLBAR_HELPERLISTMENU_BUTTON:
        valid = true;
        visible = true;
        break;
      case AppConstants.STANDARD_TOOLBAR_NAVIGATION:
        // todo ppatton seems like this should never be hit. The type === navigation in updateToolbarState
        // should have handled it, right?!
        this.navigationActionState(listStateInfo, toolbarButton);
        return;

      case AppConstants.STANDARD_TOOLBAR_UPDATE:
      case AppConstants.STANDARD_TOOLBAR_OPEN:
        if (!listSpec.busListSpec.openActionRestricted) {
          visible = true;
          if (listSpec.busListSpec.implementsCubeView && listStateInfo.selectedDataView.openFormResId !== undefined) {
            valid = true;
          } else if (!multipleRowsSelected && !noRowSelected) {
            const dataView: LMDataView = listStateInfo.dataViewSet.data[ selectedRowIndex ];
            const isTreeMoreLink = (<LMListDataView>dataView)?.isTreeMoreLink;
            valid = !dataView.isNewRow && !isTreeMoreLink;
          } else {
            valid = false;
          }
        }
        break;

      case AppConstants.STANDARD_TOOLBAR_OPTIONSMENU_BUTTON:
        valid = true;
        visible = true;
        break;

      case AppConstants.STANDARD_TOOLBAR_RELATEDMENU_BUTTON:
        if (!this.lmContextService.getContext().disallowNavigations) {
          visible = true;
          valid = !multipleRowsSelected;
        }
        break;

      case AppConstants.STANDARD_TOOLBAR_COPYROW:
        if (listStateInfo.inlineCreateEnabled) {
          if (!multipleRowsSelected && !noRowSelected) {
            const dataView: LMDataView = listStateInfo.dataViewSet.data[ selectedRowIndex ];
            visible = !dataView.isNewRow && !dataView.isSubtotalView && !dataView.isSubtotalSummaryView;
            valid = !dataView.isNewRow && !dataView.isSubtotalView && !dataView.isSubtotalSummaryView;
          }
        }
        break;

      case AppConstants.STANDARD_TOOLBAR_SAVENEW:
        if (listStateInfo.inlineCreateEnabled) {
          visible = true;
          valid = listStateInfo.inlineCreateModified;
        }
        break;

      case AppConstants.STANDARD_SAVE:
        if (listStateInfo.isUpdatable || listStateInfo.inlineCreateEnabled) {
          visible = true;
          valid = listStateInfo.isModified;
        }
        break;

      case AppConstants.STANDARD_TOOLBAR_SEARCH:
        toolbarButton.state.isPressed = listStateInfo.searchFormDisplayed;
        valid = true;
        visible = true;
        break;

      // case AppConstants.STANDARD_TOOLBAR_USERLISTVIEW_SAVE:
      //   visible = true;
      //   valid = listStateInfo.hasFilterCriteria;
      //   break;

      case AppConstants.STANDARD_TOOLBAR_USERLISTVIEW:
      case AppConstants.STANDARD_TOOLBAR_REFRESH:
      case AppConstants.STANDARD_TOOLBAR_PRINTTOFILE:
      case AppConstants.STANDARD_TOOLBAR_CREATEREPORT:
      case AppConstants.STANDARD_TOOLBAR_RELATEDREPORT:
      case AppConstants.STANDARD_TOOLBAR_PERSONALIZE:
        valid = true;
        visible = true;
        break;

      case AppConstants.STANDARD_TOOLBAR_VIEWACTION:
        break;

      case AppConstants.STANDARD_TOOLBAR_VIEWAUDIT:
      case AppConstants.STANDARD_TOOLBAR_VIEWFULLAUDIT:
        visible = true;
        valid = listStateInfo.selectedRows.length === 1;
        break;

      case AppConstants.STANDARD_TOOLBAR_AUDITCOMPARE:
        visible = listSpec.busListSpec.implementsAuditCompare;
        valid = listStateInfo.selectedRows.length === 2;
        break;

      case AppConstants.ACTION_TYPE_AUDIT_DELETE:
      case AppConstants.ACTION_TYPE_AUDIT_UPDATE:
      case AppConstants.ACTION_TYPE_CHANGE_EFFECTIVEDATE:
        visible = valid = this.isValidAuditAction(toolbarButton, listStateInfo, listSpec.busClassSpec.actions) && listStateInfo.selectedRows.length === 1 && !listSpec.busListSpec.openActionRestricted;
        break;

      case AppConstants.ACTION_TYPE_AUDIT_OVERRIDE:
        visible = valid = this.isValidAuditAction(toolbarButton, listStateInfo, listSpec.busClassSpec.actions) && listStateInfo.selectedRows.length === 1;
        break;
    }

    // check for called out action button validity
    if (action !== AppConstants.STANDARD_TOOLBAR_HELPERLIST) {
      if (multipleRowsSelected) {
        if (valid && toolbarButton.actionReference && toolbarButton.actionReference.validity) {
          listStateInfo.selectedRows.some((element, index) => {
            const dataView = listStateInfo.dataViewSet.data[element];
            if (!this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView)) {
              valid = false;
              return true;
            }
          });
        }
      } else {
        if (valid && toolbarButton.actionReference && toolbarButton.actionReference.validity) {
          let dataView: LMDataView;
          if (selectedRowIndex !== -1) {
            dataView = listStateInfo.dataViewSet.data[ selectedRowIndex ];
          } else { // use header info when validating action which do not require row selection
            dataView = listStateInfo.dataViewSet.header;
          }
          // todo akila, passing in '' for specFields, list doesn't have spec Fields, not sure what to do here.
          valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView);
        }
      }
    }

    if (action === AppConstants.STANDARD_TOOLBAR_HELPERLIST && listSpec.busListSpec.helperLists.length > 1) {
      return;
    }
    // disabled if invalid
    toolbarButton.state.disabled = !valid;

    if (toolbarButton.actionReference && visible) {
      const hideWhenInvalid = (listStateInfo.isSelectListInSelectionMode) ? true : toolbarButton?.actionReference?.hideWhenInvalid;
      toolbarButton.state.visible = !(hideWhenInvalid && !valid);
    } else {
      toolbarButton.state.visible = visible;
    }
  }

  private isValidAuditAction(toolbarButton: LMToolbarButton, listStateInfo: LMListStateInfo, actions: Array<LMActionSpec>): boolean {
    for (const i of listStateInfo.selectedDataView.actionIndexes) {
      const actionSpec = <LMActionSpec> actions[i];
      if (actionSpec && actionSpec.type === toolbarButton.actionReference.id) {
        return true;
      }
    }
    return false;
  }

  private getFormInvokeState(listStateInfo: LMListStateInfo, toolbarButton: LMToolbarButton): LMToolbarButtonState {
    const isListFormInvoke = toolbarButton.actionReference.isListFormInvoke;
    if (!isListFormInvoke && listStateInfo.selectedRows.length !== 1) {
      return {
        disabled: true,
        visible: !toolbarButton.actionReference.hideWhenInvalid
      };
    }

    const dataView = toolbarButton.actionReference.isListFormInvoke ? listStateInfo.dataViewSet.header : listStateInfo.selectedDataView;

    const valid = toolbarButton.actionReference.validity
      ? this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView)
      : true;

    return {
      disabled: !valid,
      visible: valid || !toolbarButton.actionReference.hideWhenInvalid
    };
  }

  private httpUrlActionState(listStateInfo: LMListStateInfo, toolbarButton: LMToolbarButton): void {
    const isRowUiLink = toolbarButton.actionReference.isRowUiLink;

    if (isRowUiLink && listStateInfo.selectedRows.length !== 1) {
      const visible = !toolbarButton.actionReference.hideWhenInvalid;
      toolbarButton.state = { visible, disabled: true };
      return;
    }

    const dataView: LMDataView = isRowUiLink ? listStateInfo.selectedDataView : listStateInfo.dataViewSet.header;

    const valid = toolbarButton.actionReference.validity
      ? this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView)
      : true;

    // disabled if not valid
    toolbarButton.state.disabled = !valid;

    // hide if (invalid and hideWhenInvalid === true)
    toolbarButton.state.visible = !(!valid && toolbarButton.actionReference.hideWhenInvalid);
  }

  private navigationActionState(listStateInfo: LMListStateInfo, toolbarButton: LMToolbarButton, specFields?: LMSpecFields | string): void {
    const isSpecialAction = toolbarButton.actionReference.isSpecialAction;
    const isListUiLink = toolbarButton.actionReference.isListUiLink;
    const isRowUiLink = toolbarButton.actionReference.isRowUiLink;
    // navigation is ony valid if one row is selected with the exception of a MatrixNavigation or if it's a special Action
    // For a matrixNavigation the selected rows become the columns in the matrix pattern
    // added a condition for the row link is buttons.
    if (!isSpecialAction
      && listStateInfo.selectedRows.length !== 1
      && !isListUiLink
      && !toolbarButton.actionReference.isMatrixFormButton
      || (isRowUiLink && listStateInfo.selectedRows.length !== 1)) {
      const visible = !toolbarButton.actionReference.hideWhenInvalid;
      toolbarButton.state = { visible, disabled: true };
      return;
    }

    let dataView: LMDataView = isListUiLink ? listStateInfo.dataViewSet.header : listStateInfo.selectedDataView;

    if (isSpecialAction) {
      dataView = listStateInfo.fromDataView;
    }

    let valid = false;
    if (!dataView.isSubtotalSummaryView && !dataView.isSubtotalView) {
      if (dataView.navigations) {
        valid = dataView.navigations.indexOf(toolbarButton.actionReference.navigation) > -1;
      }

      // Set special action and uilink navigations to valid
      if (isSpecialAction || isListUiLink || isRowUiLink) {
        valid = true;
      }

      // check for navigation button validity
      if (valid && toolbarButton.actionReference.validity) {
        valid = this.lmConditionUtilService.isValid(toolbarButton.actionReference.validity, dataView);
      }
    }
    // disabled if not valid
    toolbarButton.state.disabled = !valid;

    // hide if (invalid and hideWhenInvalid === true)
    toolbarButton.state.visible = !(!valid && toolbarButton.actionReference.hideWhenInvalid);
  }

  private processFlowActionState(
    listSpec: LMListSpec,
    listStateInfo: LMListStateInfo,
    toolbarButton: LMToolbarButton
  ): LMToolbarButton {
    // Validity / Visibility for process flow button.. If there is one in dataview, it is valid and visible.
    toolbarButton.state.disabled = false;
    toolbarButton.state.visible = true;

    return toolbarButton;
  }

  private cubeActionState(listSpec: LMListSpec, listStateInfo: LMListStateInfo, toolbarButton: LMToolbarButton): void {
    switch (toolbarButton.actionReference.id) {
      case AppConstants.CUBE_TOOLBAR_PERIODOVERRIDE:
        let visible = true;
        let disabled = false;
        if (listStateInfo.dataViewSet) {
          disabled = listStateInfo.dataViewSet.cubeInfo.suppressCurPeriod;
          visible = !(listStateInfo.dataViewSet.cubeInfo.currentDims.some((dim) => dim.isPeriod));
        }
        toolbarButton.state = { visible, disabled };
        break;
      default:
        toolbarButton.state = { visible: true, disabled: false };
    }
  }

  private getDisallowRelated(listSpec: LMListSpec): boolean {
    return this.lmContextService.getContext().disallowNavigations;
  }

  private buildCubeToolbarButtons(listSpec: LMListSpec, listStateInfo: LMListStateInfo): Array<LMToolbarButton> {
    const dataViewSet = listStateInfo.dataViewSet;
    const cubeActionReferences: LMBusActionReference[] = [];

    // TODO: (1) Only if Child hierarchy dimensions are present, the following menu items are available
    // TODO: (2) What about main list vs dependent list? should that distinction be made?
    if (listSpec.busListSpec.hasChildHierarchyDims) {
      // Set Child hierarchy Dimension Values
      cubeActionReferences.push({
        id: AppConstants.CUBE_TOOLBAR_SET_CH_DIMENSIONS,
        automationId: AppConstants.CUBE_TOOLBAR_SET_CH_DIMENSIONS,
        type: 'cube',
        label: Soho.Locale.translate('Structures'),
        tooltip: Soho.Locale.translate('SetCHDimensionValue')
      });

      // Set Filter Dimension values
      if (!listSpec.busListSpec.searchForm) {
        cubeActionReferences.push({
          id: AppConstants.CUBE_TOOLBAR_SET_FILTER_DIMENSIONS,
          automationId: AppConstants.CUBE_TOOLBAR_SET_FILTER_DIMENSIONS,
          type: 'cube',
          label: Soho.Locale.translate('Filter'),
          tooltip: Soho.Locale.translate('SetFilterDimensionValue')
        });
      }
    }

    // Dimension button
    cubeActionReferences.push({
      id: AppConstants.CUBE_TOOLBAR_DIMENSIONS,
      automationId: AppConstants.CUBE_TOOLBAR_DIMENSIONS,
      type: 'cube',
      label: Soho.Locale.translate('Dimensions'),
      tooltip: Soho.Locale.translate('ChangeDimensions')
    });

    // Pivot Views
    if (listSpec.cubeOptions.pivotViews) {
      let pivotView;
      if (listStateInfo.dataViewSet.cubeInfo.pivotView) {
        pivotView = listSpec.cubeOptions.pivotViews.find((p) => p.name === listStateInfo.dataViewSet.cubeInfo.pivotView);
      } else {
        pivotView = listSpec.cubeOptions.pivotViews.find((p) => p.default !== undefined && p.default);
      }
      if (!pivotView) {
        pivotView = listSpec.cubeOptions.pivotViews[ 0 ];
      }
      cubeActionReferences.push({
        id: AppConstants.CUBE_TOOLBAR_PIVOTVIEWS,
        automationId: AppConstants.CUBE_TOOLBAR_PIVOTVIEWS,
        type: 'cube',
        label: pivotView.label,
        tooltip: Soho.Locale.translate('ChangePivotViews')
      });
    }

    if (listSpec.cubeOptions.columnViews) {
      // TODO: Get the current selection from DVS
      let columnView;
      if (listStateInfo.dataViewSet.cubeInfo.columnView) {
        columnView = listSpec.cubeOptions.columnViews.find((c) => c.name === listStateInfo.dataViewSet.cubeInfo.columnView);
      } else {
        columnView = listSpec.cubeOptions.columnViews.find((c) => c.default !== undefined && c.default);
      }
      if (!columnView) {
        columnView = listSpec.cubeOptions.columnViews[ 0 ];
      }
      cubeActionReferences.push({
        id: AppConstants.CUBE_TOOLBAR_COLUMNVIEWS,
        automationId: AppConstants.CUBE_TOOLBAR_COLUMNVIEWS,
        type: 'cube',
        label: columnView.label,
        tooltip: Soho.Locale.translate('ChangeColumnViews')
      });
    }
    // Period Override
    if (listSpec.cubeOptions.hasPeriodDimension) {
      // Current Period
      const currentPeriodLabel = (dataViewSet && dataViewSet.cubeInfo) ? dataViewSet.cubeInfo.periodDimension : '';
      cubeActionReferences.push({
        id: AppConstants.CUBE_TOOLBAR_PERIODOVERRIDE,
        automationId: AppConstants.CUBE_TOOLBAR_PERIODOVERRIDE,
        type: 'cube',
        label: currentPeriodLabel,
        tooltip: Soho.Locale.translate('CurrentPeriod')
      });
    }

    // Period Views
    const hasPeriodViews = listSpec.cubeOptions.periods && listSpec.cubeOptions.periods.length > 0;
    if (hasPeriodViews) {
      let index = 0;
      if (dataViewSet && dataViewSet.cubeInfo && dataViewSet.cubeInfo.periodView) {
        const i = listSpec.cubeOptions.pCommands.indexOf(dataViewSet.cubeInfo.periodView);
        if (i !== -1) {
          index = i;
        }
      }
      const periodViewLabel = listSpec.cubeOptions.periods[index];
      cubeActionReferences.push({
        id: AppConstants.CUBE_TOOLBAR_PERIODVIEW,
        automationId: AppConstants.CUBE_TOOLBAR_PERIODVIEW,
        type: 'cube',
        label: periodViewLabel,
        tooltip: Soho.Locale.translate('PeriodViews')
      });
    }
    // allow update - save
    if (this.lmListSpecUtilService.allowUpdate(listSpec)) {
      cubeActionReferences.push(this.getStandardToolbarActions().save);
    }

    // Switch Charts
    if (this.getIncludeChartMenu(listSpec)) {
      // let chart = listSpec.busListSpec.charts[listStateInfo.cubeDisplayChartName].title;
      // let chartLabel = this.lmFormatUtilService.formatMessageRule(chart, undefined);
      const chartsMenu = this.getStandardToolbarActions().chartsMenu;
      chartsMenu.id = AppConstants.CUBE_TOOLBAR_CHARTS;
      chartsMenu.automationId = AppConstants.CUBE_TOOLBAR_CHARTS;
      chartsMenu.type = 'cube';
      // chartsMenu.icon = null; // No Icon
      // chartsMenu.label = chartLabel;
      cubeActionReferences.push(chartsMenu);
    }
    if (this.includeSearch(listSpec, listStateInfo)) {
      cubeActionReferences.push(this.getStandardToolbarActions().search);
    }

    // Saved search
    if (this.showUserListViewToolbarButton(listSpec, listStateInfo)) {
      cubeActionReferences.push(this.getStandardToolbarActions().userListViewMenu);
    }

    const toolbarButtons1 = this.actionRefArrayToToolbarButtonArray(listSpec, cubeActionReferences);

    // Options
    const optionsMenu = { ...this.getStandardToolbarActions().options };
    optionsMenu.id = AppConstants.CUBE_TOOLBAR_OPTIONS;
    optionsMenu.automationId = AppConstants.CUBE_TOOLBAR_OPTIONS;
    optionsMenu.type = 'cube';

    const toolbarButtons2 = this.actionRefArrayToToolbarButtonArray(listSpec, [ optionsMenu ], true);
    return [ ...toolbarButtons1, ...toolbarButtons2 ];
  }

  private buildCubeViewDetailSectionToolbarItem(listSpec: LMListSpec, listStateInfo: LMListStateInfo): Array<LMToolbarButton> {
    if (this.lmListSpecUtilService.isDetailSection(listSpec)
      && listStateInfo.fromDataView && listStateInfo.fromDataView.cubeInfo) {
      if (listStateInfo.fromDataView.cubeInfo.extraInfo2?.pvPeriods
        && listStateInfo.fromDataView.cubeInfo.extraInfo2.pvPeriods.length > 0) {
        const currentPVPeriod = listStateInfo.fromDataView.cubeInfo.extraInfo2.pvPeriods[listStateInfo.fromDataView.cubeInfo.extraInfo.pvPeriodIdx];
        const pvPeriodActionRef: LMBusActionReference = {
          id:   AppConstants.CUBE_TOOLBAR_PERIODVIEWPERIODS,
          automationId: AppConstants.CUBE_TOOLBAR_PERIODVIEWPERIODS,
          type: 'cube',
          label: currentPVPeriod.label,
          index: currentPVPeriod.idx,
          tooltip: Soho.Locale.translate('SelectPeriod'),
        };
        const pvPeriodToolbarButton = this.createToolbarMenuItemsFromActionReferences(listSpec, [ pvPeriodActionRef ])[0];
        pvPeriodToolbarButton.menu = this.getPeriodViewPeriodMenuItem(listSpec, listStateInfo);
        return [ pvPeriodToolbarButton ];
      }
    }
    return [];
  }

  private getPeriodViewPeriodMenuItem(listSpec: LMListSpec, listStateInfo: LMListStateInfo): Array<LMToolbarButton> {
    const currSelectionIndex = listStateInfo.fromDataView.cubeInfo.extraInfo.pvPeriodIdx;
    const pvPeriodActionRef: Array<LMBusActionReference> = listStateInfo.fromDataView.cubeInfo.extraInfo2.pvPeriods
      .map((pvPeriod) => <LMBusActionReference>{
        id: AppConstants.CUBE_TOOLBAR_PERIODVIEWPERIODS + pvPeriod.idx,
        automationId: AppConstants.CUBE_TOOLBAR_PERIODVIEWPERIODS + pvPeriod.idx,
        type: 'cube',
        label: pvPeriod.label,
        index: pvPeriod.idx,
        isChecked: (pvPeriod.idx === currSelectionIndex)
      });
    return this.createToolbarMenuItemsFromActionReferences(listSpec, pvPeriodActionRef);
  }

  private getHelperLists(listSpec: LMListSpec): LMHelperList[] {
    return listSpec.busListSpec.helperLists;
  }

  private getOpenActionRestricted(listSpec: LMListSpec) {
    return listSpec.busListSpec.openActionRestricted;
  }

  private getIncludeChartMenu(listSpec: LMListSpec) {
    const galleryViewWithDisplayFields = listSpec.busListSpec.implementsGalleryView && listSpec.busListSpec.displayFields.length > 0;
    return listSpec.busListSpec.charts || galleryViewWithDisplayFields ? true : false;
  }

  private getChartMenuOptions(listSpec: LMListSpec) {
    if (this.lmListSpecUtilService.isColumnarView(listSpec) && listSpec.busListSpec.charts) {
      const chartSpecs: LMChartSpecs = {};

      Object.keys(listSpec.busListSpec.charts).forEach((chartName) => {
        const chartType: string = listSpec.busListSpec.charts[chartName].chartType;
        if (chartType === 'bar' || chartType === 'pie' || chartType === 'gap' || chartType === 'aggregation') {
          chartSpecs[ chartName ] = JSON.parse(JSON.stringify(listSpec.busListSpec.charts[ chartName ]));
        }
      });

      return chartSpecs;
    }

    return listSpec.busListSpec.charts;
  }

  private getActions(listSpec: LMListSpec): Array<LMActionSpec> {
    return listSpec.busClassSpec.actions;
  }

  private getIncludeSaveAndNew(listStateInfo: LMListStateInfo) {
    return listStateInfo.inlineCreateEnabled;
  }

  private filterOutExcludedActions(validActionRefs: Array<LMBusActionReference>, excludedActionRefs: Array<LMBusActionReference>): Array<LMBusActionReference> {
    if (excludedActionRefs && excludedActionRefs.length > 0) {
      validActionRefs = validActionRefs
        .filter(actionRef => {
          const result = excludedActionRefs.find(item => {
            if (item.id) {
            // Need to strip off _<index> before comparing to the actionRef.id.
            // .+(?=_\d+$) - to match everything up to an _<number>
              const match = item.id.match(/.+(?=_\d+$)/);
              const itemId = match ? match.toString() : item.id;
              return itemId === actionRef.id;
            }
          });

          return result === undefined; // INO: include this if it's not in the excluded list.
        });
    }

    return validActionRefs;
  }
}
